import React, { useState, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============================================================
// API
// ============================================================
const api = {
  setToken: (t) => t ? localStorage.setItem('token', t) : localStorage.removeItem('token'),
  getToken: () => localStorage.getItem('token'),
  headers: () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${api.getToken()}` }),
  login: (email, password) =>
    fetch(`${API_URL}/api/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) }).then(r => r.json()),
  register: (name, email, password) =>
    fetch(`${API_URL}/api/auth/register`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, email, password }) }).then(r => r.json()),
  getCurrentUser: () => fetch(`${API_URL}/api/auth/me`, { headers: api.headers() }).then(r => r.json()),
  getEvents: () => fetch(`${API_URL}/api/events`, { headers: api.headers() }).then(r => r.json()),
  createEvent: (event) => fetch(`${API_URL}/api/events`, { method: 'POST', headers: api.headers(), body: JSON.stringify(event) }).then(r => r.json()),
  deleteEvent: (id) => fetch(`${API_URL}/api/events/${id}`, { method: 'DELETE', headers: api.headers() }).then(r => r.json()),
  getChores: () => fetch(`${API_URL}/api/chores`, { headers: api.headers() }).then(r => r.json()),
  createChore: (chore) => fetch(`${API_URL}/api/chores`, { method: 'POST', headers: api.headers(), body: JSON.stringify(chore) }).then(r => r.json()),
  toggleChore: (id) => fetch(`${API_URL}/api/chores/${id}/toggle`, { method: 'PATCH', headers: api.headers() }).then(r => r.json()),
  deleteChore: (id) => fetch(`${API_URL}/api/chores/${id}`, { method: 'DELETE', headers: api.headers() }).then(r => r.json()),
  getPhotos: () => fetch(`${API_URL}/api/photos`, { headers: api.headers() }).then(r => r.json()),
  uploadPhoto: (file, caption) => {
    const fd = new FormData();
    fd.append('photo', file);
    fd.append('caption', caption);
    return fetch(`${API_URL}/api/photos`, { method: 'POST', headers: { Authorization: `Bearer ${api.getToken()}` }, body: fd }).then(r => r.json());
  },
  deletePhoto: (id) => fetch(`${API_URL}/api/photos/${id}`, { method: 'DELETE', headers: api.headers() }).then(r => r.json()),
  getFamilyMembers: () => fetch(`${API_URL}/api/family-members`, { headers: api.headers() }).then(r => r.json()),
  getListItems: () => fetch(`${API_URL}/api/list-items`, { headers: api.headers() }).then(r => r.json()),
  createListItem: (name, category) => fetch(`${API_URL}/api/list-items`, { method: 'POST', headers: api.headers(), body: JSON.stringify({ name, category }) }).then(r => r.json()),
  toggleListItem: (id) => fetch(`${API_URL}/api/list-items/${id}/toggle`, { method: 'PATCH', headers: api.headers() }).then(r => r.json()),
  deleteListItem: (id) => fetch(`${API_URL}/api/list-items/${id}`, { method: 'DELETE', headers: api.headers() }).then(r => r.json()),
  getUsers: () => fetch(`${API_URL}/api/admin/users`, { headers: api.headers() }).then(r => r.json()),
  createUser: (user) => fetch(`${API_URL}/api/admin/users`, { method: 'POST', headers: api.headers(), body: JSON.stringify(user) }).then(r => r.json()),
  updateUser: (id, user) => fetch(`${API_URL}/api/admin/users/${id}`, { method: 'PUT', headers: api.headers(), body: JSON.stringify(user) }).then(r => r.json()),
  deleteUser: (id) => fetch(`${API_URL}/api/admin/users/${id}`, { method: 'DELETE', headers: api.headers() }).then(r => r.json()),
  getStats: () => fetch(`${API_URL}/api/admin/stats`, { headers: api.headers() }).then(r => r.json()),
};

// ============================================================
// HELPERS
// ============================================================
const HOUR_HEIGHT = 72; // px per hour in week grid
const START_HOUR = 6;
const END_HOUR = 23;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  d.setHours(0, 0, 0, 0);
  return d;
}

function getWeekDays(ws) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(ws);
    d.setDate(d.getDate() + i);
    return d;
  });
}

// Returns a flat 42-day (6×7) grid for the month containing `date`
function getMonthGrid(date) {
  const y = date.getFullYear(), mo = date.getMonth();
  const firstOfMonth = new Date(y, mo, 1);
  const dow = firstOfMonth.getDay(); // 0=Sun
  const offset = dow === 0 ? 6 : dow - 1; // shift so Mon=0
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(y, mo, 1 - offset + i);
    return d;
  });
}

// 6-week rolling grid for the roster day-picker
function getPickerWeeks(n = 6) {
  const start = getWeekStart(new Date());
  return Array.from({ length: n }, (_, w) =>
    Array.from({ length: 7 }, (_, d) => {
      const day = new Date(start);
      day.setDate(day.getDate() + w * 7 + d);
      return day;
    })
  );
}

const SHIFT_TEMPLATES = {
  jake: [
    { label: 'Day Shift',       time: '06:00', suffix: 'Day Shift'       },
    { label: 'Afternoon Shift', time: '14:00', suffix: 'Afternoon Shift' },
    { label: 'Night Shift',     time: '22:00', suffix: 'Night Shift'     },
    { label: 'OVP',             time: '07:00', suffix: 'OVP'             },
    { label: 'Car Park',        time: '20:00', suffix: 'Car Park Shift'  },
  ],
  christie: [
    { label: 'Day Shift',   time: '06:30', suffix: 'Day Shift'   },
    { label: 'Arvo Shift',  time: '15:00', suffix: 'Arvo Shift'  },
    { label: 'Night Shift', time: '22:30', suffix: 'Night Shift' },
  ],
  isla: [
    { label: 'Dancing',      time: '15:45', suffix: 'Dancing'          },
    { label: 'Swimming',     time: '11:00', suffix: 'Swimming'         },
    { label: 'Audiologist',  time: '10:45', suffix: 'Audiologist'      },
    { label: 'Hearing Aus',  time: '09:00', suffix: 'Hearing Australia' },
    { label: 'ENT Review',   time: '10:00', suffix: 'ENT Review'       },
  ],
};

const ROSTER_PEOPLE = [
  { key: 'jake',     label: 'Jake',     hex: '#2b8cee' },
  { key: 'christie', label: 'Christie', hex: '#ec4899' },
  { key: 'isla',     label: 'Isla',     hex: '#f59e0b' },
];

function toLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDate(val) {
  if (!val) return '';
  // Plain YYYY-MM-DD string (what the backend now returns)
  if (typeof val === 'string' && val.length === 10) return val;
  // ISO string with time — recover local date to avoid UTC-offset shift
  if (typeof val === 'string') return toLocalDate(new Date(val));
  if (val instanceof Date) return toLocalDate(val);
  return '';
}

function getEventTop(time) {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  if (h < START_HOUR || h >= END_HOUR) return null;
  return (h - START_HOUR + m / 60) * HOUR_HEIGHT;
}

function fmt12(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ap}`;
}

// Formats a HH:MM shift time (no seconds needed)
function fmtShift(time) { return fmt12(time + ':00'); }

function fmt24Hour(h) {
  return `${String(h).padStart(2, '0')}:00`;
}

// Full Tailwind class names (must be complete strings for CDN JIT)
const COLORS = {
  jake:     { pill: 'bg-blue-100 border-l-4 border-blue-400',    title: 'text-blue-700',  sub: 'text-blue-500',  ring: 'ring-blue-400',  avatar: 'bg-blue-500'  },
  christie: { pill: 'bg-pink-100 border-l-4 border-pink-400',    title: 'text-pink-700',  sub: 'text-pink-500',  ring: 'ring-pink-400',  avatar: 'bg-pink-500'  },
  isla:     { pill: 'bg-amber-100 border-l-4 border-amber-400',  title: 'text-amber-700', sub: 'text-amber-500', ring: 'ring-amber-400', avatar: 'bg-amber-500' },
  default:  { pill: 'bg-green-100 border-l-4 border-green-400',  title: 'text-green-700', sub: 'text-green-500', ring: 'ring-green-400', avatar: 'bg-green-500' },
  nana:     { pill: 'bg-purple-100 border-l-4 border-purple-400',title: 'text-purple-700',sub: 'text-purple-500',ring: 'ring-purple-400',avatar: 'bg-purple-500'},
};

function eventColor(title = '') {
  const t = title.toLowerCase();
  if (t.includes('jake'))     return COLORS.jake;
  if (t.includes('christie')) return COLORS.christie;
  if (t.includes('isla'))     return COLORS.isla;
  if (t.includes('nana'))     return COLORS.nana;
  return COLORS.default;
}

function memberColor(name = '') {
  const n = name.toLowerCase();
  if (n.includes('jake'))     return COLORS.jake;
  if (n.includes('christie')) return COLORS.christie;
  if (n.includes('isla'))     return COLORS.isla;
  if (n.includes('nana'))     return COLORS.nana;
  return COLORS.default;
}

function memberHex(name = '') {
  const n = (name || '').toLowerCase();
  if (n.includes('jake'))     return '#2b8cee';
  if (n.includes('christie')) return '#ec4899';
  if (n.includes('isla'))     return '#f59e0b';
  if (n.includes('nana'))     return '#a855f7';
  return '#22c55e';
}

const Icon = ({ name, className = '' }) => (
  <span className={`material-symbols-outlined select-none ${className}`}>{name}</span>
);

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuth, setShowAuth]       = useState(true);
  const [authMode, setAuthMode]       = useState('login');
  const [currentView, setCurrentView] = useState('calendar');

  // Auth form
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [uname, setUname]       = useState('');
  const [authError, setAuthError] = useState('');

  // Data
  const [events, setEvents]               = useState([]);
  const [chores, setChores]               = useState([]);
  const [photos, setPhotos]               = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);

  // Calendar
  const [weekStart, setWeekStart]     = useState(getWeekStart(new Date()));
  const [calendarView, setCalendarView] = useState('week'); // 'week' | 'month'
  const [monthStart, setMonthStart]   = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [mobileDay, setMobileDay]         = useState(new Date());
  const [mobileCalView, setMobileCalView] = useState('week'); // 'day' | 'week' | 'month'
  const [mobileMonthStart, setMobileMonthStart] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [isMobile, setIsMobile]       = useState(() => window.innerWidth < 768);
  const [now, setNow] = useState(new Date());

  // Modals
  const [showEventModal, setShowEventModal] = useState(false);
  const [showChoreModal, setShowChoreModal] = useState(false);
  const [selectedEvent, setSelectedEvent]   = useState(null);
  const [selectedPhoto, setSelectedPhoto]   = useState(null);
  const [photoFilter, setPhotoFilter]       = useState('all');
  const [slideshowActive, setSlideshowActive] = useState(false);
  const [slideshowIndex, setSlideshowIndex]   = useState(0);
  const inactivityTimerRef = useRef(null);

  // Quick-add tabs + roster state
  const [addModalTab, setAddModalTab]   = useState('event');
  const [rosterPerson, setRosterPerson] = useState('');
  const [rosterShift, setRosterShift]   = useState(null);
  const [rosterDays, setRosterDays]     = useState(new Set());
  const [rosterSaving, setRosterSaving] = useState(false);

  // Forms
  const [eventForm, setEventForm] = useState({ title: '', date: '', time: '', description: '' });
  const [choreForm, setChoreForm] = useState({ title: '', assigned_to: '', due_date: '', notes: '' });

  // Shopping / Lists
  const LIST_CATEGORIES = [
    { name: 'Produce',      color: '#4ade80' },
    { name: 'Dairy & Eggs', color: '#60a5fa' },
    { name: 'Pantry',       color: '#fbbf24' },
    { name: 'Other',        color: '#a855f7' },
  ];
  const [listItems, setListItems]           = useState([]);
  const [listInput, setListInput]           = useState('');
  const [listCategory, setListCategory]     = useState('Produce');
  const [showPurchased, setShowPurchased]   = useState(false);

  async function addListItem() {
    const name = listInput.trim();
    if (!name) return;
    setListInput('');
    try {
      const item = await api.createListItem(name, listCategory);
      if (item && !item.error) setListItems(prev => [item, ...prev]);
    } catch (e) { console.error(e); }
  }

  async function toggleListItem(id) {
    // Optimistic update
    setListItems(prev => prev.map(item =>
      item.id === id ? { ...item, checked: !item.checked, checked_at: !item.checked ? new Date().toISOString() : null } : item
    ));
    try {
      const updated = await api.toggleListItem(id);
      if (updated && !updated.error) {
        setListItems(prev => prev.map(item => item.id === id ? updated : item));
      }
    } catch (e) { console.error(e); }
  }

  async function deleteListItem(id) {
    setListItems(prev => prev.filter(item => item.id !== id));
    try { await api.deleteListItem(id); } catch (e) { console.error(e); }
  }

  // Admin
  const [users, setUsers]           = useState([]);
  const [stats, setStats]           = useState({});
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm]     = useState({ name: '', email: '', password: '', is_admin: false });
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Inactivity timer → auto-start slideshow (screen-saver style)
  useEffect(() => {
    function onActivity() {
      setSlideshowActive(false);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        setSlideshowActive(true);
        setSlideshowIndex(0);
      }, 60000); // 60 s of inactivity
    }
    const evts = ['mousemove', 'mousedown', 'keydown', 'touchstart'];
    evts.forEach(e => window.addEventListener(e, onActivity));
    onActivity(); // start timer on mount
    return () => {
      evts.forEach(e => window.removeEventListener(e, onActivity));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, []); // eslint-disable-line

  // Auto-advance slideshow every 4 s
  useEffect(() => {
    if (!slideshowActive || photos.length === 0) return;
    const t = setInterval(() => setSlideshowIndex(p => (p + 1) % photos.length), 4000);
    return () => clearInterval(t);
  }, [slideshowActive, photos.length]);

  // ESC closes slideshow
  useEffect(() => {
    if (!slideshowActive) return;
    const onKey = e => { if (e.key === 'Escape') setSlideshowActive(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slideshowActive]);

  // Clock tick
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (api.getToken()) loadCurrentUser(); }, []);
  useEffect(() => { if (currentUser?.is_admin && currentView === 'admin') loadAdminData(); }, [currentView, currentUser]); // eslint-disable-line

  async function loadCurrentUser() {
    try {
      const user = await api.getCurrentUser();
      if (user.error) { handleLogout(); return; }
      setCurrentUser(user);
      setShowAuth(false);
      await loadAllData();
    } catch { handleLogout(); }
  }

  async function loadAllData() {
    const safe = (p) => p.catch(e => { console.error(e); return []; });
    const [ev, ch, ph, mb, li] = await Promise.all([
      safe(api.getEvents()),
      safe(api.getChores()),
      safe(api.getPhotos()),
      safe(api.getFamilyMembers()),
      safe(api.getListItems()),
    ]);
    setEvents(Array.isArray(ev) ? ev : []);
    setChores(Array.isArray(ch) ? ch : []);
    setPhotos(Array.isArray(ph) ? ph : []);
    setFamilyMembers(Array.isArray(mb) ? mb : []);
    setListItems(Array.isArray(li) ? li : []);
  }

  async function loadAdminData() {
    try {
      const [us, st] = await Promise.all([api.getUsers(), api.getStats()]);
      setUsers(Array.isArray(us) ? us : []);
      setStats(st || {});
    } catch (e) { console.error(e); }
  }

  const handleAuth = async () => {
    setAuthError('');
    try {
      const result = authMode === 'login'
        ? await api.login(email, password)
        : await api.register(uname, email, password);
      if (result.error) { setAuthError(result.error); return; }
      api.setToken(result.token);
      setCurrentUser(result.user);
      setShowAuth(false);
      await loadAllData();
      setEmail(''); setPassword(''); setUname('');
    } catch { setAuthError('An error occurred. Please try again.'); }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    api.setToken(null);
    setShowAuth(true);
    setCurrentView('calendar');
  };

  // Opens the Quick Add modal, optionally pre-filling date/time from a double-click
  const openQuickAdd = (date = '', time = '', tab = 'event') => {
    setEventForm({ title: '', date, time, description: '' });
    setAddModalTab(tab);
    setRosterPerson('');
    setRosterShift(null);
    setRosterDays(new Set());
    setShowEventModal(true);
  };

  const closeQuickAdd = () => {
    setShowEventModal(false);
    setEventForm({ title: '', date: '', time: '', description: '' });
    setRosterPerson('');
    setRosterShift(null);
    setRosterDays(new Set());
  };

  const addEvent = async () => {
    try {
      const e = await api.createEvent(eventForm);
      if (!e.error) { setEvents(prev => [...prev, e]); closeQuickAdd(); }
    } catch (e) { console.error(e); }
  };

  const addRosterShifts = async () => {
    if (!rosterPerson || !rosterShift || rosterDays.size === 0) return;
    setRosterSaving(true);
    try {
      const personLabel = ROSTER_PEOPLE.find(p => p.key === rosterPerson)?.label || rosterPerson;
      const title = `${personLabel} ${rosterShift.suffix}`;
      const results = await Promise.all(
        [...rosterDays].map(date => api.createEvent({ title, date, time: rosterShift.time, description: '' }))
      );
      setEvents(prev => [...prev, ...results.filter(r => !r.error)]);
      closeQuickAdd();
    } catch (e) { console.error(e); }
    setRosterSaving(false);
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await api.deleteEvent(id); setEvents(prev => prev.filter(e => e.id !== id)); setSelectedEvent(null); } catch (e) { console.error(e); }
  };

  const addChore = async () => {
    try {
      const c = await api.createChore(choreForm);
      if (!c.error) { setChores(prev => [...prev, c]); setShowChoreModal(false); setChoreForm({ title: '', assigned_to: '', due_date: '', notes: '' }); }
    } catch (e) { console.error(e); }
  };

  const toggleChore = async (id) => {
    try {
      const c = await api.toggleChore(id);
      if (!c.error) setChores(prev => prev.map(x => x.id === id ? c : x));
    } catch (e) { console.error(e); }
  };

  const deleteChore = async (id) => {
    if (!window.confirm('Delete this chore?')) return;
    try { await api.deleteChore(id); setChores(prev => prev.filter(c => c.id !== id)); } catch (e) { console.error(e); }
  };

  const handlePhotoUpload = async (e) => {
    for (const file of Array.from(e.target.files)) {
      try {
        const p = await api.uploadPhoto(file, '');
        if (!p.error) setPhotos(prev => [...prev, p]);
      } catch (e) { console.error(e); }
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    try { await api.deletePhoto(id); setPhotos(prev => prev.filter(p => p.id !== id)); setSelectedPhoto(null); } catch (e) { console.error(e); }
  };

  const handleUserSubmit = async () => {
    try {
      if (editingUser) {
        const u = await api.updateUser(editingUser.id, { name: userForm.name, email: userForm.email, is_admin: userForm.is_admin });
        if (!u.error) setUsers(prev => prev.map(x => x.id === editingUser.id ? u : x));
      } else {
        const u = await api.createUser(userForm);
        if (!u.error) setUsers(prev => [...prev, u]);
      }
      setShowUserModal(false);
      setUserForm({ name: '', email: '', password: '', is_admin: false });
      setEditingUser(null);
      await loadAdminData();
    } catch (e) { console.error(e); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await api.deleteUser(id); setUsers(prev => prev.filter(u => u.id !== id)); await loadAdminData(); } catch (e) { console.error(e); }
  };

  const weekDays   = getWeekDays(weekStart);
  const todayStr   = toLocalDate(new Date());
  const pendingCnt = chores.filter(c => !c.completed).length;
  const timeStr    = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
  const dateStr    = now.toLocaleDateString('en-AU', { day: 'numeric', month: 'long' });
  const dayStr     = now.toLocaleDateString('en-AU', { weekday: 'long' });
  const monthLabel = weekStart.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });

  // ---- AUTH SCREEN ----
  if (showAuth) {
    const quickMembers = [
      { name: 'Jake',     color: '#3b82f6', ring: '#93c5fd' },
      { name: 'Christie', color: '#ec4899', ring: '#f9a8d4' },
      { name: 'Isla',     color: '#f59e0b', ring: '#fcd34d' },
    ];

    return (
      <div className="min-h-screen flex items-center justify-center p-4"
           style={{ fontFamily: 'Inter, sans-serif', backgroundColor: '#f6f7f8' }}>
        {/* Background blobs */}
        <div className="fixed -top-24 -right-24 w-96 h-96 rounded-full blur-3xl pointer-events-none"
             style={{ background: 'rgba(0,93,168,0.06)' }} />
        <div className="fixed -bottom-24 -left-24 w-96 h-96 rounded-full blur-3xl pointer-events-none"
             style={{ background: 'rgba(80,95,118,0.06)' }} />

        <main className="w-full flex overflow-hidden rounded-xl border border-slate-200"
              style={{ maxWidth: 1100, height: 720, background: '#fff',
                       boxShadow: '0 10px 25px -5px rgba(0,95,172,0.12), 0 8px 10px -6px rgba(0,95,172,0.1)' }}>

          {/* Left panel */}
          <section className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-shrink-0">
            {/* Gradient hero instead of external image */}
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(160deg, #0a4a8a 0%, #1a6cc4 45%, #3b9de0 100%)'
            }} />
            {/* Decorative circles */}
            <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full opacity-10"
                 style={{ background: 'white' }} />
            <div className="absolute bottom-20 -left-10 w-48 h-48 rounded-full opacity-10"
                 style={{ background: 'white' }} />
            <div className="absolute top-1/3 left-1/2 w-64 h-64 rounded-full opacity-5"
                 style={{ background: 'white' }} />
            <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
              <div className="mb-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                     style={{ background: 'rgba(255,255,255,0.2)' }}>
                  <Icon name="home_filled" className="text-xl" />
                </div>
                <span className="text-sm font-semibold opacity-80">Wicks Family Hub</span>
              </div>
              <h1 className="font-extrabold mb-4 leading-tight" style={{ fontSize: 36 }}>
                Your family,<br />beautifully organised.
              </h1>
              <p className="opacity-80 max-w-sm leading-relaxed text-sm">
                The central command for your modern household. Calendars, chores, and memories — all in one place.
              </p>
              <div className="mt-8 flex gap-2">
                <div className="h-1 w-12 bg-white rounded-full" />
                <div className="h-1 w-4 rounded-full" style={{ background: 'rgba(255,255,255,0.35)' }} />
                <div className="h-1 w-4 rounded-full" style={{ background: 'rgba(255,255,255,0.35)' }} />
              </div>
            </div>
          </section>

          {/* Right panel */}
          <section className="w-full lg:w-1/2 flex flex-col p-8 md:p-14 justify-center overflow-auto"
                   style={{ background: '#f8f9ff' }}>

            {/* Logo */}
            <header className="mb-10">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                     style={{ background: '#005da8' }}>
                  <Icon name="grid_view" className="text-xl" />
                </div>
                <span className="font-extrabold text-base tracking-tight" style={{ color: '#005da8' }}>
                  Wicks Family Hub
                </span>
              </div>
              <h2 className="font-bold text-slate-900" style={{ fontSize: 30, lineHeight: 1 }}>
                {authMode === 'login' ? 'Welcome back' : 'Create account'}
              </h2>
              <p className="text-sm mt-2" style={{ color: '#505f76' }}>
                {authMode === 'login'
                  ? 'Please enter your details to access the hub.'
                  : 'Fill in your details to join the family hub.'}
              </p>
            </header>

            {/* Error */}
            {authError && (
              <div className="mb-4 text-sm rounded-lg px-4 py-3" style={{ background: '#ffdad6', color: '#93000a' }}>
                {authError}
              </div>
            )}

            {/* Form */}
            <form className="space-y-5" onSubmit={e => { e.preventDefault(); handleAuth(); }}>
              {authMode === 'register' && (
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#505f76' }}>
                    Full Name
                  </label>
                  <div className="relative">
                    <Icon name="person" className="absolute left-3 text-lg" style={{ top: '50%', transform: 'translateY(-50%)', color: '#717784' }} />
                    <input className="w-full pl-10 pr-4 py-3 rounded-lg border text-sm focus:outline-none focus:ring-2 transition-all"
                           style={{ background: '#f1f3fc', borderColor: '#c0c7d4', color: '#181c22' }}
                           placeholder="Your full name" value={uname} onChange={e => setUname(e.target.value)} />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#505f76' }}>
                  Email Address
                </label>
                <div className="relative">
                  <Icon name="mail" className="absolute left-3 text-lg" style={{ top: '50%', transform: 'translateY(-50%)', color: '#717784' }} />
                  <input className="w-full pl-10 pr-4 py-3 rounded-lg border text-sm focus:outline-none focus:ring-2 transition-all"
                         style={{ background: '#f1f3fc', borderColor: '#c0c7d4', color: '#181c22' }}
                         type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-xs font-bold uppercase tracking-widest" style={{ color: '#505f76' }}>
                    Password
                  </label>
                </div>
                <div className="relative">
                  <Icon name="lock" className="absolute left-3 text-lg" style={{ top: '50%', transform: 'translateY(-50%)', color: '#717784' }} />
                  <input className="w-full pl-10 pr-4 py-3 rounded-lg border text-sm focus:outline-none focus:ring-2 transition-all"
                         style={{ background: '#f1f3fc', borderColor: '#c0c7d4', color: '#181c22' }}
                         type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
                </div>
              </div>

              <button type="submit"
                      className="w-full py-3.5 text-white font-bold text-sm rounded-lg flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95"
                      style={{ background: '#005da8', boxShadow: '0 4px 14px rgba(0,93,168,0.25)' }}>
                {authMode === 'login' ? 'Sign In' : 'Create Account'}
                <Icon name="arrow_forward" className="text-lg" />
              </button>
            </form>

            {/* Quick access — only show on login */}
            {authMode === 'login' && (
              <div className="mt-10 pt-8 border-t border-slate-200">
                <h3 className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: '#505f76' }}>
                  Quick Access
                </h3>
                <div className="flex gap-5">
                  {quickMembers.map(m => (
                    <button key={m.name}
                            onClick={() => setEmail(m.name.toLowerCase() + '@wickshub.com')}
                            className="flex flex-col items-center gap-2 group transition-transform active:scale-95">
                      <div className="w-14 h-14 rounded-full p-0.5 border-2 transition-all group-hover:shadow-md flex items-center justify-center"
                           style={{ borderColor: m.ring, background: '#ebeef6' }}>
                        <div className="w-full h-full rounded-full flex items-center justify-center text-white font-bold text-lg"
                             style={{ background: m.color }}>
                          {m.name.charAt(0)}
                        </div>
                      </div>
                      <span className="text-xs font-bold uppercase tracking-widest transition-colors group-hover:text-blue-700"
                            style={{ color: '#505f76' }}>
                        {m.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Toggle login / register */}
            <p className="text-sm mt-6" style={{ color: '#717784' }}>
              {authMode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button className="font-semibold hover:underline" style={{ color: '#005da8' }}
                      onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setAuthError(''); }}>
                {authMode === 'login' ? 'Register' : 'Sign In'}
              </button>
            </p>

            {/* Footer */}
            <footer className="mt-auto pt-6 flex justify-between items-center" style={{ color: '#717784' }}>
              <p style={{ fontSize: 11 }}>© {new Date().getFullYear()} Wicks Family Hub</p>
              <div className="flex gap-4" style={{ fontSize: 11 }}>
                <a href="#" className="hover:underline">Privacy</a>
                <a href="#" className="hover:underline">Help</a>
              </div>
            </footer>
          </section>
        </main>
      </div>
    );
  }

  // Derived mobile state (cheap to compute on every render)
  const mobileDayStr     = toLocalDate(mobileDay);
  const mobileDayEvents  = events.filter(e => parseDate(e.date) === mobileDayStr);
  const isMobileToday    = mobileDayStr === todayStr;
  const mobileDayLabel   = mobileDay.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' });
  const mobileFamilyStats = (familyMembers.length > 0 ? familyMembers : [
    { name: 'Jake' }, { name: 'Christie' }, { name: 'Isla' },
  ]).map(m => {
    const mine = chores.filter(c => (c.assigned_to || '').toLowerCase().includes(m.name.toLowerCase()));
    return { ...m, total: mine.length, done: mine.filter(c => c.completed).length };
  });

  // ---- MAIN LAYOUT ----
  return (
    <>
      {isMobile ? (
        /* ===== MOBILE LAYOUT ===== */
        <div style={{ fontFamily: 'Inter, sans-serif' }} className="flex flex-col min-h-screen bg-gray-50 text-slate-900">

          {/* Mobile Header */}
          <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-md px-4 pt-5 pb-3 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold tracking-tight text-slate-900">Wicks Family</h1>
              <div className="flex items-center gap-2">
                <div className="bg-blue-50 px-3 py-1 rounded-full">
                  <p className="text-blue-600 font-bold text-sm">{timeStr}</p>
                </div>
                <button onClick={handleLogout} className="p-1.5 rounded-lg text-slate-400">
                  <Icon name="logout" className="text-lg" />
                </button>
              </div>
            </div>
          </header>

          {/* ---- MOBILE CALENDAR ---- */}
          {currentView === 'calendar' && (
            <>
              {/* Family carousel */}
              <section className="px-4 pt-4">
                <div className="flex gap-4 overflow-x-auto py-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {mobileFamilyStats.map((m, i) => {
                    const hex = memberHex(m.name);
                    const pct = m.total > 0 ? Math.round((m.done / m.total) * 100) : 100;
                    return (
                      <div key={i} className="flex flex-col items-center gap-1.5 min-w-[64px]">
                        <div className="p-0.5 rounded-full" style={{ border: `2.5px solid ${hex}` }}>
                          <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-base"
                               style={{ background: hex }}>
                            {m.name.charAt(0).toUpperCase()}
                          </div>
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-semibold">{m.name}</p>
                          <div className="w-10 h-1.5 bg-slate-200 rounded-full mt-0.5 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: hex }} />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">{m.done}/{m.total || '—'}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Nav bar — month header or week strip depending on active view */}
              {(() => {
                const mws = getWeekStart(mobileDay);
                const mwDays = getWeekDays(mws);
                const DAY_INITIALS = ['M','T','W','T','F','S','S'];
                const viewToggle = (
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                    {[['day','Day'],['week','Week'],['month','Month']].map(([v, label]) => (
                      <button key={v} onClick={() => {
                        if (v === 'month') {
                          const ms = new Date(mobileDay.getFullYear(), mobileDay.getMonth(), 1);
                          setMobileMonthStart(ms);
                        }
                        setMobileCalView(v);
                      }}
                        className={`px-2.5 py-1 text-xs font-semibold transition-colors ${
                          mobileCalView === v ? 'bg-blue-500 text-white' : 'text-slate-500 hover:bg-slate-50'
                        }`}>
                        {label}
                      </button>
                    ))}
                  </div>
                );

                if (mobileCalView === 'month') {
                  return (
                    <div className="px-3 pt-3 pb-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setMobileMonthStart(p => new Date(p.getFullYear(), p.getMonth() - 1, 1))}
                            className="p-1 rounded-lg bg-slate-100 text-slate-600 active:bg-slate-200">
                            <Icon name="chevron_left" className="text-lg" />
                          </button>
                          <span className="text-sm font-bold text-slate-700 px-1">
                            {mobileMonthStart.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
                          </span>
                          <button onClick={() => setMobileMonthStart(p => new Date(p.getFullYear(), p.getMonth() + 1, 1))}
                            className="p-1 rounded-lg bg-slate-100 text-slate-600 active:bg-slate-200">
                            <Icon name="chevron_right" className="text-lg" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setMobileDay(new Date()); setMobileMonthStart(new Date(new Date().getFullYear(), new Date().getMonth(), 1)); }}
                            className="text-blue-500 text-xs font-semibold">Today</button>
                          {viewToggle}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="px-3 pt-4 pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1">
                        <button onClick={() => { const d = new Date(mws); d.setDate(d.getDate() - 7); setMobileDay(d); }}
                          className="p-1 rounded-lg bg-slate-100 text-slate-600 active:bg-slate-200">
                          <Icon name="chevron_left" className="text-lg" />
                        </button>
                        <span className="text-sm font-bold text-slate-700 px-1">
                          {mws.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
                        </span>
                        <button onClick={() => { const d = new Date(mws); d.setDate(d.getDate() + 7); setMobileDay(d); }}
                          className="p-1 rounded-lg bg-slate-100 text-slate-600 active:bg-slate-200">
                          <Icon name="chevron_right" className="text-lg" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        {mobileDayStr !== todayStr && (
                          <button onClick={() => setMobileDay(new Date())} className="text-blue-500 text-xs font-semibold">Today</button>
                        )}
                        {viewToggle}
                      </div>
                    </div>
                    {/* 7-day date strip */}
                    <div className="grid grid-cols-7 gap-1">
                      {mwDays.map((d, i) => {
                        const ds = toLocalDate(d);
                        const isSelected = ds === mobileDayStr;
                        const isToday2 = ds === todayStr;
                        const hasEvents = events.some(e => parseDate(e.date) === ds);
                        return (
                          <button key={i} onClick={() => setMobileDay(d)}
                            className={`flex flex-col items-center py-1.5 rounded-xl transition-colors ${
                              isSelected ? 'bg-blue-500' : isToday2 ? 'bg-blue-50' : 'hover:bg-slate-100'
                            }`}>
                            <span className={`text-[10px] font-semibold ${isSelected ? 'text-blue-100' : 'text-slate-400'}`}>
                              {DAY_INITIALS[i]}
                            </span>
                            <span className={`text-sm font-bold ${isSelected ? 'text-white' : isToday2 ? 'text-blue-600' : 'text-slate-700'}`}>
                              {d.getDate()}
                            </span>
                            <div className={`w-1 h-1 rounded-full mt-0.5 ${hasEvents ? (isSelected ? 'bg-white' : 'bg-blue-400') : 'bg-transparent'}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* ---- DAY TIMELINE VIEW ---- */}
              {mobileCalView === 'day' && (
                <main className="flex-1 px-4 pb-32 overflow-y-auto">
                  {mobileDayEvents.filter(e => !e.time).length > 0 && (
                    <div className="mb-3 space-y-1.5 pt-2">
                      {mobileDayEvents.filter(e => !e.time).map(ev => {
                        const c = eventColor(ev.title);
                        return (
                          <div key={ev.id} onClick={() => setSelectedEvent(ev)}
                            className={`${c.pill} p-3 rounded-xl cursor-pointer`}>
                            <p className={`text-sm font-bold ${c.title}`}>{ev.title}</p>
                            <p className={`text-xs ${c.sub}`}>All day</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div>
                    {HOURS.map(h => {
                      const hourEvents = mobileDayEvents.filter(e => e.time && parseInt(e.time.split(':')[0]) === h);
                      const isCurrentHour = isMobileToday && now.getHours() === h;
                      const nowPct = isCurrentHour ? Math.round((now.getMinutes() / 60) * 100) : null;
                      return (
                        <div key={h} className="flex gap-3" style={{ minHeight: 56 }}>
                          <div className="w-12 pt-1 text-right flex-shrink-0">
                            <span className={`text-xs font-medium ${isCurrentHour ? 'text-blue-500 font-bold' : 'text-slate-400'}`}>
                              {h === 12 ? '12 PM' : h < 12 ? `${h} AM` : `${h - 12} PM`}
                            </span>
                          </div>
                          <div className="flex-1 relative border-t border-slate-100 pt-1 pb-2"
                            onDoubleClick={e => {
                              if (e.target.closest('[data-event]')) return;
                              openQuickAdd(mobileDayStr, String(h).padStart(2, '0') + ':00');
                            }}>
                            {isCurrentHour && nowPct !== null && (
                              <div className="absolute left-0 right-0 flex items-center z-10 pointer-events-none"
                                   style={{ top: `${nowPct}%` }}>
                                <div className="w-2 h-2 rounded-full bg-blue-500 -ml-1 flex-shrink-0" />
                                <div className="flex-1 h-0.5 bg-blue-500" />
                              </div>
                            )}
                            {hourEvents.map(ev => {
                              const c = eventColor(ev.title);
                              return (
                                <div key={ev.id} data-event="1" onClick={() => setSelectedEvent(ev)}
                                  className={`${c.pill} p-3 rounded-xl mb-1.5 cursor-pointer`}>
                                  <p className={`text-sm font-bold ${c.title}`}>{ev.title}</p>
                                  <p className={`text-xs mt-0.5 ${c.sub}`}>{fmt12(ev.time)}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </main>
              )}

              {/* ---- WEEK AGENDA VIEW ---- */}
              {mobileCalView === 'week' && (() => {
                const mws = getWeekStart(mobileDay);
                const mwDays = getWeekDays(mws);
                return (
                  <main className="flex-1 px-4 pb-32 overflow-y-auto">
                    {mwDays.map((day, di) => {
                      const ds = toLocalDate(day);
                      const isToday2 = ds === todayStr;
                      const isSelected = ds === mobileDayStr;
                      const dayEvs = events.filter(e => parseDate(e.date) === ds);
                      const DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
                      return (
                        <div key={di} className="mb-3"
                          onDoubleClick={e => { if (!e.target.closest('[data-event]')) openQuickAdd(ds); }}>
                          <div
                            className={`flex items-center gap-2 py-2 px-1 rounded-lg cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                            onClick={() => { setMobileDay(day); setMobileCalView('day'); }}>
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                              isToday2 ? 'bg-blue-500 text-white' : isSelected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {day.getDate()}
                            </div>
                            <div>
                              <p className={`text-sm font-bold ${isToday2 ? 'text-blue-600' : 'text-slate-700'}`}>
                                {DAY_NAMES[di]} {day.getDate()} {isToday2 && <span className="text-xs font-normal text-blue-400">Today</span>}
                              </p>
                            </div>
                            {dayEvs.length === 0 && <span className="ml-auto text-xs text-slate-300">No events</span>}
                          </div>
                          {dayEvs.length > 0 && (
                            <div className="ml-2 pl-9 space-y-1.5">
                              {dayEvs
                                .slice()
                                .sort((a, b) => (a.time || '00:00') < (b.time || '00:00') ? -1 : 1)
                                .map(ev => {
                                  const c = eventColor(ev.title);
                                  return (
                                    <div key={ev.id} data-event="1" onClick={() => setSelectedEvent(ev)}
                                      className={`${c.pill} p-2.5 rounded-xl cursor-pointer`}>
                                      <p className={`text-sm font-bold ${c.title}`}>{ev.title}</p>
                                      {ev.time && <p className={`text-xs mt-0.5 ${c.sub}`}>{fmt12(ev.time)}</p>}
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </main>
                );
              })()}

              {/* ---- MONTH GRID VIEW ---- */}
              {mobileCalView === 'month' && (() => {
                const grid = getMonthGrid(mobileMonthStart);
                const curMonth = mobileMonthStart.getMonth();
                const DAY_NAMES = ['M','T','W','T','F','S','S'];
                return (
                  <main className="flex-1 px-3 pb-32 overflow-y-auto">
                    {/* Day name headers */}
                    <div className="grid grid-cols-7 mb-1">
                      {DAY_NAMES.map(d => (
                        <div key={d} className="py-1 text-center text-[10px] font-bold uppercase tracking-wider text-slate-400">{d}</div>
                      ))}
                    </div>
                    {/* 6-week grid */}
                    <div className="grid grid-cols-7" style={{ gridAutoRows: '72px' }}>
                      {grid.map((day, i) => {
                        const ds = toLocalDate(day);
                        const isToday2  = ds === todayStr;
                        const isSelected = ds === mobileDayStr;
                        const inMonth   = day.getMonth() === curMonth;
                        const dayEvs    = events.filter(e => parseDate(e.date) === ds);
                        return (
                          <div key={i}
                            className={`border border-slate-100 p-1 overflow-hidden cursor-pointer transition-colors ${
                              isSelected ? 'bg-blue-50' : inMonth ? 'bg-white' : 'bg-slate-50/60'
                            }`}
                            onClick={() => { setMobileDay(day); setMobileCalView('day'); }}>
                            <div className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full mb-0.5 ${
                              isToday2 ? 'bg-blue-500 text-white' : isSelected ? 'bg-blue-100 text-blue-700' : inMonth ? 'text-slate-800' : 'text-slate-300'
                            }`}>
                              {day.getDate()}
                            </div>
                            <div className="space-y-0.5">
                              {dayEvs.slice(0, 2).map(ev => {
                                const c = eventColor(ev.title);
                                return (
                                  <div key={ev.id}
                                    onClick={e => { e.stopPropagation(); setSelectedEvent(ev); }}
                                    className={`text-[9px] font-semibold px-1 py-0.5 rounded truncate ${c.pill} ${c.title}`}>
                                    {ev.title}
                                  </div>
                                );
                              })}
                              {dayEvs.length > 2 && (
                                <div className="text-[9px] text-slate-400 pl-0.5">+{dayEvs.length - 2}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </main>
                );
              })()}

              {/* FAB */}
              <button onClick={() => openQuickAdd(mobileDayStr)}
                className="fixed bottom-24 right-5 w-14 h-14 text-white rounded-full shadow-xl flex items-center justify-center z-30 active:scale-95 transition-transform"
                style={{ background: '#2b8cee' }}>
                <Icon name="add" className="text-3xl" />
              </button>
            </>
          )}

          {/* ---- MOBILE CHORES ---- */}
          {currentView === 'chores' && (() => {
            const activeChores    = chores.filter(c => !c.completed);
            const completedChores = chores.filter(c => c.completed);
            const pendingCnt2     = activeChores.length;

            // Per-member stats for the carousel
            const choreMembers = (familyMembers.length > 0 ? familyMembers : [
              { name: 'Jake' }, { name: 'Christie' }, { name: 'Isla' },
            ]).map(m => {
              const mine = chores.filter(c => (c.assigned_to || '').toLowerCase().includes(m.name.toLowerCase()));
              const done = mine.filter(c => c.completed).length;
              return { ...m, total: mine.length, done };
            }).filter(m => m.total > 0);

            return (
              <main className="flex-1 pb-28 overflow-y-auto">

                {/* Title */}
                <div className="px-4 pt-4 pb-2">
                  <h1 className="text-3xl font-extrabold text-slate-900">Chores</h1>
                  <p className="text-xs text-slate-400 font-medium mt-0.5">Household Daily Command</p>
                </div>

                {/* Family member progress carousel */}
                {choreMembers.length > 0 && (
                  <div className="flex gap-3 overflow-x-auto px-4 py-2" style={{ scrollbarWidth: 'none' }}>
                    {choreMembers.map((m, i) => {
                      const hex = memberHex(m.name);
                      const pct = m.total > 0 ? Math.round((m.done / m.total) * 100) : 0;
                      const allDone = m.done === m.total;
                      return (
                        <div key={i} className="flex-shrink-0 bg-white p-3 rounded-xl border border-slate-100 shadow-sm w-36">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                                 style={{ background: hex, border: `2px solid ${hex}` }}>
                              {m.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-bold text-sm text-slate-800 truncate">{m.name}</span>
                          </div>
                          <p className="text-[10px] text-slate-400 mb-1">
                            {allDone ? 'All done!' : `${m.done}/${m.total} done`}
                          </p>
                          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: hex }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="px-4 space-y-5 mt-2">

                  {/* Active chores */}
                  <section>
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Active Chores</h2>
                      {pendingCnt2 > 0 && (
                        <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full uppercase tracking-wide">
                          {pendingCnt2} Pending
                        </span>
                      )}
                    </div>

                    {activeChores.length === 0 && (
                      <div className="text-center py-10">
                        <Icon name="task_alt" className="text-4xl text-slate-200 block mx-auto mb-2" />
                        <p className="text-slate-400 text-sm">All chores complete!</p>
                      </div>
                    )}

                    <div className="space-y-3">
                      {activeChores.map(ch => {
                        const hex = memberHex(ch.assigned_to || '');
                        const c   = memberColor(ch.assigned_to || '');
                        return (
                          <div key={ch.id}
                            className="bg-white rounded-xl shadow-sm flex items-center justify-between p-4 active:scale-[0.98] transition-all"
                            style={{ borderLeft: `4px solid ${hex}` }}>
                            <div className="flex-1 min-w-0 mr-3">
                              <p className="font-bold text-slate-900 text-sm">{ch.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {ch.assigned_to && (
                                  <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: hex }}>
                                    {ch.assigned_to}
                                  </span>
                                )}
                                {ch.assigned_to && ch.due_date && <span className="w-1 h-1 rounded-full bg-slate-200 flex-shrink-0" />}
                                {ch.due_date && (
                                  <span className="text-[11px] text-slate-400">{ch.due_date}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button onClick={() => toggleChore(ch.id)}
                                className="w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all active:scale-90"
                                style={{ borderColor: hex }}>
                                <Icon name="check" className="text-base transition-all" style={{ color: hex }} />
                              </button>
                              <button onClick={() => deleteChore(ch.id)}
                                className="w-7 h-7 rounded-full flex items-center justify-center text-slate-300 active:text-red-400 transition-colors">
                                <Icon name="delete" className="text-base" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>

                  {/* Completed chores */}
                  {completedChores.length > 0 && (
                    <section>
                      <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Completed Lately</h3>
                      <div className="space-y-2">
                        {completedChores.map(ch => {
                          const hex = memberHex(ch.assigned_to || '');
                          return (
                            <div key={ch.id}
                              className="bg-white rounded-xl p-3 flex items-center gap-3 opacity-50 border border-slate-100">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                   style={{ background: hex }}>
                                <Icon name="check" className="text-white text-base" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold line-through text-slate-400 truncate">{ch.title}</p>
                                {ch.assigned_to && <p className="text-[10px] text-slate-300">{ch.assigned_to}</p>}
                              </div>
                              <button onClick={() => toggleChore(ch.id)}
                                className="text-slate-300 active:text-blue-400 transition-colors p-1">
                                <Icon name="undo" className="text-base" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}

                </div>

                {/* FAB */}
                <button onClick={() => setShowChoreModal(true)}
                  className="fixed bottom-24 right-5 w-14 h-14 text-white rounded-full shadow-xl flex items-center justify-center z-30 active:scale-95 transition-transform"
                  style={{ background: '#2b8cee' }}>
                  <Icon name="add_task" className="text-2xl" />
                </button>

              </main>
            );
          })()}

          {/* ---- MOBILE PHOTOS ---- */}
          {currentView === 'photos' && (
            <main className="flex-1 overflow-y-auto pb-28">
              {/* ── Album Header ── */}
              <div className="px-4 pt-4 mb-5">
                <div className="flex justify-between items-end mb-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Shared Memories</p>
                    <h2 className="text-2xl font-bold text-slate-900 mt-0.5">Photo Album</h2>
                  </div>
                  {/* Family member avatar stack */}
                  <div className="flex -space-x-2">
                    {familyMembers.slice(0, 3).map(m => (
                      <div key={m.id}
                        className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-[10px] text-white font-bold shadow-sm"
                        style={{ background: memberHex(m.name) }}>
                        {m.name.charAt(0).toUpperCase()}
                      </div>
                    ))}
                    {familyMembers.length > 3 && (
                      <div className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-[10px] text-slate-600 font-bold">
                        +{familyMembers.length - 3}
                      </div>
                    )}
                  </div>
                </div>

                {/* Filter pills */}
                <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                  {[
                    { key: 'all',        label: 'All Photos', icon: 'all_inclusive' },
                    { key: 'favourites', label: 'Favourites', icon: 'star'          },
                    { key: 'people',     label: 'People',     icon: 'face'          },
                  ].map(f => (
                    <button key={f.key}
                      onClick={() => setPhotoFilter(f.key)}
                      className={`flex items-center gap-1 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                        photoFilter === f.key
                          ? 'text-white shadow-md'
                          : 'bg-white border border-slate-200 text-slate-500'
                      }`}
                      style={photoFilter === f.key ? { background: '#2b8cee' } : {}}>
                      <Icon name={f.icon} className="text-base" />
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Photo Grid ── */}
              {photos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                  <Icon name="photo_library" className="text-5xl text-slate-200 mb-3" />
                  <p className="text-slate-400 text-sm">No photos yet.</p>
                  <label className="mt-4 text-white px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer shadow-md"
                    style={{ background: '#2b8cee' }}>
                    <Icon name="add_a_photo" className="text-base align-middle mr-1" />
                    Upload Photo
                    <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                  </label>
                </div>
              ) : (
                <div className="px-4">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-base font-bold text-slate-900">All Photos</h3>
                    <div className="h-px flex-1 bg-slate-100" />
                    <span className="text-xs text-slate-400">{photos.length} photo{photos.length !== 1 ? 's' : ''}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* First photo — full width hero */}
                    {photos.slice(0, 1).map(p => (
                      <div key={p.id} onClick={() => setSelectedPhoto(p)}
                        className="col-span-2 relative rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
                        style={{ aspectRatio: '16/9' }}>
                        <img src={`${API_URL}/uploads/${p.filename}`} alt={p.caption || ''}
                          className="w-full h-full object-cover" />
                        {p.uploaded_by && (
                          <div className="absolute bottom-2 left-2 flex items-center gap-1.5 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-lg">
                            <div className="w-4 h-4 rounded-full border border-white/50 flex-shrink-0"
                              style={{ background: memberHex(p.uploaded_by) }} />
                            <span className="text-[10px] text-white font-medium">{p.uploaded_by.split(' ')[0]} uploaded</span>
                          </div>
                        )}
                        {p.caption && (
                          <div className="absolute bottom-2 right-2 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-lg">
                            <span className="text-[10px] text-white">{p.caption}</span>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Remaining photos — 2-column squares */}
                    {photos.slice(1).map(p => (
                      <div key={p.id} onClick={() => setSelectedPhoto(p)}
                        className="relative rounded-2xl overflow-hidden aspect-square cursor-pointer active:scale-[0.96] transition-transform">
                        <img src={`${API_URL}/uploads/${p.filename}`} alt={p.caption || ''}
                          className="w-full h-full object-cover" />
                        {p.uploaded_by && (
                          <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 bg-black/30 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
                            <div className="w-3 h-3 rounded-full border border-white/50 flex-shrink-0"
                              style={{ background: memberHex(p.uploaded_by) }} />
                            <span className="text-[9px] text-white font-medium">{p.uploaded_by.split(' ')[0]}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAB: Upload Photo */}
              <label className="fixed bottom-24 right-5 w-14 h-14 text-white rounded-full flex items-center justify-center shadow-xl cursor-pointer z-40 active:scale-90 transition-transform"
                style={{ background: '#2b8cee' }}>
                <Icon name="add_a_photo" className="text-2xl" />
                <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
              </label>
            </main>
          )}

          {/* ---- MOBILE LISTS ---- */}
          {currentView === 'lists' && (() => {
            const activeItems  = listItems.filter(i => !i.checked);
            const checkedItems = listItems.filter(i => i.checked)
              .sort((a, b) => new Date(b.checked_at) - new Date(a.checked_at));
            const activeCats   = LIST_CATEGORIES.filter(c => activeItems.some(i => i.category === c.name));

            return (
              <main className="flex-1 overflow-y-auto pb-32">
                {/* Sticky quick-add bar */}
                <div className="sticky top-0 z-40 px-4 pt-3 pb-2 bg-gray-50">
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-2 flex items-center gap-2">
                    <Icon name="shopping_basket" className="text-slate-400 ml-1 text-xl" />
                    <input
                      className="flex-1 bg-transparent border-none focus:ring-0 text-sm text-slate-800 placeholder-slate-400"
                      placeholder="Add new item…"
                      value={listInput}
                      onChange={e => setListInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addListItem()}
                    />
                    <select
                      className="text-xs border-none bg-slate-100 rounded-lg px-2 py-1.5 text-slate-500 focus:ring-0 cursor-pointer"
                      value={listCategory}
                      onChange={e => setListCategory(e.target.value)}>
                      {LIST_CATEGORIES.map(c => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={addListItem}
                      className="text-white px-4 py-1.5 rounded-lg text-sm font-bold active:scale-95 transition-transform"
                      style={{ background: '#2b8cee' }}>
                      Add
                    </button>
                  </div>
                </div>

                <div className="px-4 pt-2 space-y-7">
                  {/* Empty state */}
                  {activeItems.length === 0 && checkedItems.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <Icon name="shopping_basket" className="text-5xl text-slate-200 mb-3" />
                      <p className="text-slate-400 text-sm">Your list is empty.<br/>Add your first item above!</p>
                    </div>
                  )}

                  {/* Active items by category */}
                  {activeCats.map(cat => {
                    const catItems = activeItems.filter(i => i.category === cat.name);
                    return (
                      <div key={cat.name}>
                        <div className="flex items-center justify-between mb-3 px-1">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ background: cat.color }} />
                            <h2 className="text-xs font-bold uppercase tracking-widest" style={{ color: '#2b8cee' }}>
                              {cat.name}
                            </h2>
                          </div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            {catItems.length} {catItems.length === 1 ? 'ITEM' : 'ITEMS'}
                          </span>
                        </div>

                        <div className="space-y-2">
                          {catItems.map(item => (
                            <div key={item.id}
                              className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 shadow-sm active:scale-[0.98] transition-transform"
                              onClick={() => toggleListItem(item.id)}>
                              <div className="flex items-center gap-4">
                                {/* Custom checkbox */}
                                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
                                  item.checked ? 'border-blue-500 bg-blue-500' : 'border-blue-500'
                                }`}>
                                  {item.checked && (
                                    <Icon name="check" className="text-white text-sm" />
                                  )}
                                </div>
                                <div>
                                  <p className={`text-sm text-slate-800 ${item.checked ? 'line-through text-slate-400' : ''}`}>
                                    {item.name}
                                  </p>
                                  <div className="flex items-center mt-0.5 gap-1">
                                    <span className="w-2 h-2 rounded-full inline-block"
                                      style={{ background: memberHex(item.added_by) }} />
                                    <span className="text-[11px] text-slate-400">
                                      {item.added_by.split(' ')[0]}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={e => { e.stopPropagation(); deleteListItem(item.id); }}
                                className="text-slate-300 active:text-red-400 p-1">
                                <Icon name="delete" className="text-lg" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}

                  {/* Recently Purchased (collapsible) */}
                  {checkedItems.length > 0 && (
                    <div className="border-t border-slate-200 pt-5">
                      <button
                        className="flex items-center justify-between w-full text-left mb-3"
                        onClick={() => setShowPurchased(p => !p)}>
                        <span className="text-sm font-semibold text-slate-500">
                          Recently Purchased ({checkedItems.length})
                        </span>
                        <Icon name={showPurchased ? 'expand_less' : 'expand_more'} className="text-slate-400 text-xl" />
                      </button>

                      {showPurchased && (
                        <div className="space-y-2">
                          {checkedItems.map(item => (
                            <div key={item.id}
                              className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl opacity-60"
                              onClick={() => toggleListItem(item.id)}>
                              <Icon name="check_circle" className="text-blue-400 text-xl flex-shrink-0" />
                              <p className="text-sm text-slate-500 line-through flex-1">{item.name}</p>
                              <button
                                onClick={e => { e.stopPropagation(); deleteListItem(item.id); }}
                                className="text-slate-300 active:text-red-400 p-1">
                                <Icon name="delete" className="text-lg" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </main>
            );
          })()}

          {/* ---- MOBILE SETTINGS ---- */}
          {currentView === 'settings' && (
            <main className="flex-1 px-4 pt-4 pb-28 overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Settings</h2>
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm divide-y divide-slate-100">
                <div className="px-4 py-4 flex items-center gap-3">
                  <div className={`size-10 rounded-full flex items-center justify-center text-white font-bold ${memberColor(currentUser?.name || '').avatar}`}>
                    {(currentUser?.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{currentUser?.name}</p>
                    <p className="text-xs text-slate-400">{currentUser?.email}</p>
                  </div>
                </div>
                {currentUser?.is_admin && (
                  <button onClick={() => setCurrentView('admin')}
                    className="w-full px-4 py-4 flex items-center gap-3 text-left text-sm font-medium text-slate-700">
                    <Icon name="shield_person" className="text-slate-400 text-xl" />
                    Admin Panel
                    <Icon name="chevron_right" className="text-slate-300 ml-auto text-xl" />
                  </button>
                )}
                <button onClick={handleLogout}
                  className="w-full px-4 py-4 flex items-center gap-3 text-left text-sm font-medium text-red-500">
                  <Icon name="logout" className="text-red-400 text-xl" />
                  Sign Out
                </button>
              </div>
            </main>
          )}

          {/* ---- MOBILE ADMIN ---- */}
          {currentView === 'admin' && currentUser?.is_admin && (
            <main className="flex-1 px-4 pt-4 pb-28 overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Admin</h2>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { label: 'Users',  value: stats.users  || 0, icon: 'group',               color: 'text-blue-600 bg-blue-50'  },
                  { label: 'Events', value: stats.events || 0, icon: 'calendar_today',       color: 'text-green-600 bg-green-50' },
                  { label: 'Chores', value: stats.chores || 0, icon: 'assignment_turned_in', color: 'text-amber-600 bg-amber-50' },
                  { label: 'Photos', value: stats.photos || 0, icon: 'photo_library',        color: 'text-pink-600 bg-pink-50'  },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className={`size-8 rounded-lg flex items-center justify-center mb-2 ${s.color}`}>
                      <Icon name={s.icon} className="text-sm" />
                    </div>
                    <p className="text-xl font-bold text-slate-900">{s.value}</p>
                    <p className="text-slate-500 text-xs">{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900 text-sm">Users</h3>
                  <button onClick={() => { setEditingUser(null); setUserForm({ name: '', email: '', password: '', is_admin: false }); setShowUserModal(true); }}
                    className="text-blue-500 text-sm font-semibold flex items-center gap-1">
                    <Icon name="add" className="text-base" />Add
                  </button>
                </div>
                <div className="divide-y divide-slate-100">
                  {users.map(u => (
                    <div key={u.id} className="px-4 py-3 flex items-center gap-3">
                      <div className={`size-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${memberColor(u.name).avatar}`}>
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{u.name}</p>
                        <p className="text-xs text-slate-400 truncate">{u.email}</p>
                      </div>
                      {u.id !== currentUser.id && (
                        <button onClick={() => deleteUser(u.id)} className="p-1.5 text-slate-300 active:text-red-400">
                          <Icon name="delete" className="text-base" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </main>
          )}

          {/* Mobile Bottom Nav */}
          <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-2 pb-5 pt-3 z-40">
            <div className="flex justify-around items-center max-w-md mx-auto">
              {[
                { id: 'calendar', icon: 'calendar_month',      label: 'Calendar' },
                { id: 'chores',   icon: 'checklist',            label: 'Chores'   },
                { id: 'photos',   icon: 'photo_library',        label: 'Photos'   },
                { id: 'lists',    icon: 'format_list_bulleted', label: 'Lists'    },
                { id: 'settings', icon: 'settings',             label: 'Settings' },
              ].map(({ id, icon, label }) => (
                <button key={id} onClick={() => setCurrentView(id)}
                  className={`flex flex-col items-center gap-1 transition-colors ${currentView === id ? 'text-blue-500' : 'text-slate-400'}`}>
                  <Icon name={icon} className="text-2xl" />
                  <span className="text-[10px] font-medium">{label}</span>
                </button>
              ))}
            </div>
          </nav>

        </div>
      ) : (
        /* ===== DESKTOP LAYOUT ===== */
        <div style={{ fontFamily: 'Inter, sans-serif' }} className="flex h-screen w-full bg-gray-50 text-slate-900 overflow-hidden">

      {/* Sidebar */}
      <aside className="w-64 border-r border-slate-200 bg-white flex flex-col p-4 flex-shrink-0">
        <div className="flex items-center gap-3 px-2 py-4 mb-6">
          <div className="size-10 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ background: '#2b8cee' }}>
            <Icon name="home_filled" className="text-xl" />
          </div>
          <div>
            <h1 className="text-slate-900 text-sm font-bold leading-none">Wicks Family</h1>
            <p className="text-slate-500 text-xs mt-0.5">Smart Home Hub</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1">
          {[
            { id: 'calendar', icon: 'calendar_today',        label: 'Calendar' },
            { id: 'chores',   icon: 'assignment_turned_in',  label: 'Chores'   },
            { id: 'photos',   icon: 'photo_library',         label: 'Photos'   },
            { id: 'lists',    icon: 'list_alt',              label: 'Lists'    },
          ].map(({ id, icon, label }) => (
            <button key={id} onClick={() => setCurrentView(id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                currentView === id
                  ? 'text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              style={currentView === id ? { background: '#2b8cee' } : {}}>
              <Icon name={icon} className="text-xl" />
              {label}
            </button>
          ))}
          {currentUser?.is_admin && (
            <button onClick={() => setCurrentView('admin')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                currentView === 'admin' ? 'text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
              style={currentView === 'admin' ? { background: '#2b8cee' } : {}}>
              <Icon name="shield_person" className="text-xl" />
              Admin
            </button>
          )}
        </nav>
        <div className="pt-4 border-t border-slate-100 space-y-1">
          <button onClick={() => setCurrentView('settings')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium">
            <Icon name="settings" className="text-xl" />
            Settings
          </button>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium">
            <Icon name="logout" className="text-xl" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Header */}
        <header className="h-20 border-b border-slate-200 bg-white flex items-center justify-between px-8 flex-shrink-0 z-10">
          <div className="flex flex-col">
            <span className="text-3xl font-bold text-slate-900 leading-none">{timeStr}</span>
            <span className="text-slate-500 text-sm font-medium mt-1">{dateStr} &middot; {dayStr}</span>
          </div>
          <div className="flex items-center gap-4">
            {/* Family avatars */}
            <div className="flex -space-x-2 mr-2">
              {(familyMembers.length > 0 ? familyMembers : [
                { name: 'Jake' }, { name: 'Christie' }, { name: 'Isla' }
              ]).slice(0, 5).map((m, i) => {
                const c = memberColor(m.name);
                return (
                  <div key={i} className="group relative">
                    <div className={`size-10 rounded-full border-4 border-white flex items-center justify-center text-white text-sm font-bold ring-2 ${c.ring} ${c.avatar}`}>
                      {m.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      {m.name}
                    </span>
                  </div>
                );
              })}
            </div>
            <button className="flex items-center justify-center p-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
              <Icon name="notifications" />
            </button>
            <button onClick={() => { setCurrentView('calendar'); openQuickAdd(); }}
              className="text-white px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 hover:opacity-90 transition-all shadow-md text-sm"
              style={{ background: '#2b8cee' }}>
              <Icon name="add_circle" className="text-xl" />
              Add Event
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">

          {/* ---- CALENDAR VIEW ---- */}
          {currentView === 'calendar' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {/* Calendar nav bar */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                {calendarView === 'week' ? (
                  <h2 className="text-lg font-bold text-slate-900">{monthLabel}</h2>
                ) : (
                  <h2 className="text-lg font-bold text-slate-900">
                    {monthStart.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
                  </h2>
                )}
                <div className="flex items-center gap-2">
                  {/* View toggle */}
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden mr-1">
                    {['week', 'month'].map(v => (
                      <button key={v} onClick={() => setCalendarView(v)}
                        className={`px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
                          calendarView === v ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-50'
                        }`}>
                        {v}
                      </button>
                    ))}
                  </div>
                  {/* Today */}
                  <button onClick={() => {
                    setWeekStart(getWeekStart(new Date()));
                    const d = new Date();
                    setMonthStart(new Date(d.getFullYear(), d.getMonth(), 1));
                  }}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
                    Today
                  </button>
                  {/* Prev */}
                  <button onClick={() => {
                    if (calendarView === 'week') {
                      const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d);
                    } else {
                      setMonthStart(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
                    }
                  }} className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                    <Icon name="chevron_left" />
                  </button>
                  {/* Next */}
                  <button onClick={() => {
                    if (calendarView === 'week') {
                      const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d);
                    } else {
                      setMonthStart(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
                    }
                  }} className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                    <Icon name="chevron_right" />
                  </button>
                </div>
              </div>

              {/* ---- MONTH VIEW ---- */}
              {calendarView === 'month' && (() => {
                const grid = getMonthGrid(monthStart);
                const curMonth = monthStart.getMonth();
                const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                return (
                  <div>
                    {/* Day name headers */}
                    <div className="grid grid-cols-7 border-b border-slate-100">
                      {DAY_NAMES.map(d => (
                        <div key={d} className="py-2 text-center text-xs font-bold uppercase tracking-wider text-slate-400">
                          {d}
                        </div>
                      ))}
                    </div>
                    {/* 6-week grid */}
                    <div className="grid grid-cols-7" style={{ gridAutoRows: '110px' }}>
                      {grid.map((day, i) => {
                        const ds = toLocalDate(day);
                        const isToday2   = ds === todayStr;
                        const inMonth   = day.getMonth() === curMonth;
                        const dayEvents = events.filter(e => parseDate(e.date) === ds);
                        return (
                          <div key={i}
                            className={`border-r border-b border-slate-100 last:border-r-0 p-1 overflow-hidden cursor-pointer hover:bg-slate-50 transition-colors ${
                              inMonth ? '' : 'bg-slate-50/60'
                            }`}
                            onDoubleClick={() => openQuickAdd(ds)}
                            onClick={() => { setCalendarView('week'); setWeekStart(getWeekStart(day)); }}>
                            <div className={`text-sm font-bold mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                              isToday2 ? 'bg-blue-500 text-white' : inMonth ? 'text-slate-800' : 'text-slate-300'
                            }`}>
                              {day.getDate()}
                            </div>
                            <div className="space-y-0.5">
                              {dayEvents.slice(0, 3).map(ev => {
                                const c = eventColor(ev.title);
                                return (
                                  <div key={ev.id}
                                    onClick={e => { e.stopPropagation(); setSelectedEvent(ev); }}
                                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded truncate cursor-pointer ${c.pill} ${c.title}`}>
                                    {ev.time ? fmt12(ev.time).replace(' AM','a').replace(' PM','p') + ' ' : ''}{ev.title}
                                  </div>
                                );
                              })}
                              {dayEvents.length > 3 && (
                                <div className="text-[10px] text-slate-400 pl-1">+{dayEvents.length - 3} more</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* ---- WEEK VIEW ---- */}
              {calendarView === 'week' && (<>
              {/* Day headers */}
              <div className="grid border-b border-slate-100" style={{ gridTemplateColumns: '72px repeat(7, 1fr)' }}>
                <div className="p-4 bg-slate-50 border-r border-slate-100" />
                {weekDays.map((day, i) => {
                  const ds = toLocalDate(day);
                  const isToday = ds === todayStr;
                  const dayName = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][i];
                  const evCount = events.filter(e => parseDate(e.date) === ds).length;
                  return (
                    <div key={i} className={`p-4 text-center border-r border-slate-100 last:border-r-0 ${isToday ? 'bg-blue-50' : ''}`}>
                      <p className={`text-xs font-bold uppercase tracking-wider ${isToday ? 'text-blue-500' : 'text-slate-400'}`}>{dayName}</p>
                      <p className={`text-lg font-bold mt-0.5 ${isToday ? 'text-blue-600' : 'text-slate-900'}`}>{day.getDate()}</p>
                      {evCount > 0 && (
                        <div className="mt-1 flex justify-center">
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${isToday ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>
                            {evCount}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Time grid */}
              <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
                <div className="relative grid" style={{ gridTemplateColumns: '72px repeat(7, 1fr)', minWidth: '800px' }}>
                  {/* Hour labels */}
                  <div className="border-r border-slate-100 bg-slate-50">
                    {HOURS.map(h => (
                      <div key={h} className="border-b border-slate-100 flex items-start justify-end pr-3 pt-1"
                        style={{ height: `${HOUR_HEIGHT}px` }}>
                        <span className="text-xs text-slate-400 font-medium">{fmt24Hour(h)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Day columns */}
                  {weekDays.map((day, di) => {
                    const ds = toLocalDate(day);
                    const isToday = ds === todayStr;
                    const dayEvents = events.filter(e => parseDate(e.date) === ds);

                    // Current time indicator
                    const nowTop = isToday && now.getHours() >= START_HOUR && now.getHours() < END_HOUR
                      ? (now.getHours() - START_HOUR + now.getMinutes() / 60) * HOUR_HEIGHT
                      : null;

                    return (
                      <div key={di}
                        className={`relative border-r border-slate-100 last:border-r-0 ${isToday ? 'bg-blue-50/30' : ''}`}
                        style={{ minHeight: `${HOURS.length * HOUR_HEIGHT}px` }}
                        onDoubleClick={e => {
                          if (e.target.closest('[data-event]')) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const relY  = e.clientY - rect.top;
                          const hour  = Math.max(START_HOUR, Math.min(END_HOUR - 1, START_HOUR + Math.floor(relY / HOUR_HEIGHT)));
                          openQuickAdd(ds, String(hour).padStart(2, '0') + ':00');
                        }}>
                        {/* Hour lines */}
                        {HOURS.map(h => (
                          <div key={h} className="absolute left-0 right-0 border-b border-slate-100"
                            style={{ top: `${(h - START_HOUR) * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }} />
                        ))}

                        {/* Current time line */}
                        {nowTop !== null && (
                          <div className="absolute left-0 right-0 z-20 flex items-center pointer-events-none"
                            style={{ top: `${nowTop}px` }}>
                            <div className="size-2 rounded-full bg-blue-500 -ml-1 shadow-sm flex-shrink-0" />
                            <div className="flex-1 h-0.5 bg-blue-500" />
                          </div>
                        )}

                        {/* Events */}
                        {dayEvents.map(ev => {
                          const top = getEventTop(ev.time);
                          if (top === null) {
                            // All-day or no-time events: show at top
                            return null;
                          }
                          const c = eventColor(ev.title);
                          return (
                            <div key={ev.id} data-event="1"
                              onClick={() => setSelectedEvent(ev)}
                              className={`absolute left-1 right-1 rounded-lg p-2 cursor-pointer hover:shadow-md transition-shadow z-10 ${c.pill}`}
                              style={{ top: `${top + 2}px`, minHeight: `${HOUR_HEIGHT - 6}px` }}>
                              <p className={`text-xs font-bold leading-tight ${c.title}`}>{ev.title}</p>
                              {ev.time && <p className={`text-xs mt-0.5 ${c.sub}`}>{fmt12(ev.time)}</p>}
                            </div>
                          );
                        })}

                        {/* All-day / no-time events as chips at top */}
                        {dayEvents.filter(ev => !ev.time || getEventTop(ev.time) === null).length > 0 && (
                          <div className="absolute top-1 left-1 right-1 z-10 space-y-0.5">
                            {dayEvents.filter(ev => !ev.time || getEventTop(ev.time) === null).map(ev => {
                              const c = eventColor(ev.title);
                              return (
                                <div key={ev.id} onClick={() => setSelectedEvent(ev)}
                                  className={`rounded px-2 py-1 cursor-pointer text-xs font-bold truncate ${c.pill} ${c.title}`}>
                                  {ev.title}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              </>)}
            </div>
          )}

          {/* ---- CHORES VIEW ---- */}
          {currentView === 'chores' && (() => {
            // Per-member stats derived from real chore data
            const assignees = [...new Set(chores.map(c => c.assigned_to).filter(Boolean))];
            const memberStats = assignees.map(name => {
              const mine = chores.filter(c => c.assigned_to === name);
              const done = mine.filter(c => c.completed).length;
              return { name, total: mine.length, done };
            });
            const completedChores = chores.filter(c => c.completed);
            const totalDone = completedChores.length;
            const totalAll  = chores.length;
            const overallPct = totalAll > 0 ? Math.round((totalDone / totalAll) * 100) : 0;
            const rewardPct  = Math.min(overallPct, 100);

            return (
              <div className="flex gap-6 h-full min-h-0">
                {/* ---- Left: main content ---- */}
                <div className="flex-1 flex flex-col gap-8 min-w-0">

                  {/* Household Overview */}
                  <section>
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-base font-bold text-slate-900 flex items-center gap-3">
                        <span className="w-2.5 h-7 rounded-full inline-block flex-shrink-0" style={{ background: '#2b8cee' }} />
                        Household Overview
                      </h3>
                      <span className="text-xs font-medium text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                        Overall: {overallPct}% Complete
                      </span>
                    </div>
                    {memberStats.length === 0 ? (
                      <p className="text-sm text-slate-400">No assignees yet — add chores to see overview.</p>
                    ) : (
                      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(memberStats.length, 4)}, 1fr)` }}>
                        {memberStats.map(({ name, total, done }) => {
                          const hex = memberHex(name);
                          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                          return (
                            <div key={name} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
                              <div className="flex items-center gap-3 mb-4">
                                <div className="size-11 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0 border-2"
                                     style={{ background: hex, borderColor: hex }}>
                                  {name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-bold text-slate-900">{name}</p>
                                  <p className="text-xs text-slate-400">{done}/{total} Done</p>
                                </div>
                              </div>
                              <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-700"
                                     style={{ width: `${pct}%`, background: hex }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>

                  {/* Active Chores grid */}
                  <section className="flex-1 flex flex-col min-h-0">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-base font-bold text-slate-900 flex items-center gap-3">
                        <span className="w-2.5 h-7 rounded-full inline-block flex-shrink-0" style={{ background: '#2b8cee' }} />
                        Active Chores
                      </h3>
                      <button onClick={() => setShowChoreModal(true)}
                        className="flex items-center gap-2 text-white px-5 py-2 rounded-full text-sm font-bold shadow-md hover:opacity-90 transition-all"
                        style={{ background: '#2b8cee', boxShadow: '0 4px 12px rgba(43,140,238,0.3)' }}>
                        <Icon name="add_task" className="text-lg" />
                        New Chore
                      </button>
                    </div>

                    {chores.length === 0 ? (
                      <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center flex flex-col items-center gap-3 text-slate-400">
                        <Icon name="assignment_turned_in" className="text-5xl" />
                        <p className="font-medium">No chores yet</p>
                      </div>
                    ) : (
                      <div className="grid gap-4 overflow-y-auto pb-4"
                           style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                        {chores.map(chore => {
                          const hex = memberHex(chore.assigned_to);
                          const isChecked = chore.completed;
                          return (
                            <div key={chore.id}
                                 className={`bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between hover:shadow-lg transition-all border-l-4 ${isChecked ? 'opacity-60' : ''}`}
                                 style={{ borderLeftColor: hex }}>
                              <div className="flex justify-between items-start mb-5">
                                <div className="flex flex-col min-w-0 pr-2">
                                  {chore.assigned_to && (
                                    <span className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: hex }}>
                                      {chore.assigned_to}
                                    </span>
                                  )}
                                  <h4 className={`text-sm font-bold leading-tight ${isChecked ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                                    {chore.title}
                                  </h4>
                                  {chore.notes && (
                                    <p className="text-xs text-slate-400 mt-1 truncate">{chore.notes}</p>
                                  )}
                                </div>
                                <button onClick={() => toggleChore(chore.id)}
                                  className="size-9 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all"
                                  style={isChecked
                                    ? { borderColor: hex, background: hex + '20', color: hex }
                                    : { borderColor: '#e0e2eb', color: '#c0c7d4' }}>
                                  <Icon name="check_circle" className="text-xl"
                                        style={isChecked ? { fontVariationSettings: "'FILL' 1" } : {}} />
                                </button>
                              </div>
                              <div className="flex items-center justify-between">
                                {chore.due_date ? (
                                  <div className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full"
                                       style={isChecked
                                         ? { background: hex + '10', color: hex }
                                         : { background: '#f1f3fc', color: '#505f76' }}>
                                    <Icon name={isChecked ? 'task_alt' : 'calendar_today'} className="text-base" />
                                    <span className="font-medium">{isChecked ? 'Done!' : parseDate(chore.due_date)}</span>
                                  </div>
                                ) : (
                                  <span />
                                )}
                                <div className="flex items-center gap-1">
                                  {chore.assigned_to && (
                                    <div className="size-7 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow-sm"
                                         style={{ background: hex }}>
                                      {chore.assigned_to.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                  <button onClick={() => deleteChore(chore.id)}
                                    className="size-7 rounded-full flex items-center justify-center text-slate-300 hover:text-red-400 hover:bg-red-50 transition-colors">
                                    <Icon name="delete" className="text-base" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {/* Quick add card */}
                        <button onClick={() => setShowChoreModal(true)}
                          className="bg-white/60 border-2 border-dashed border-slate-200 p-5 rounded-2xl flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-blue-500 hover:border-blue-300 hover:bg-white transition-all">
                          <Icon name="add_circle" className="text-4xl" />
                          <span className="text-sm font-bold">Quick Add Task</span>
                        </button>
                      </div>
                    )}
                  </section>
                </div>

                {/* ---- Right sidebar ---- */}
                <div className="flex flex-col gap-5 flex-shrink-0" style={{ width: 300 }}>

                  {/* Completed Lately */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden" style={{ maxHeight: 380 }}>
                    <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                      <Icon name="task_alt" className="text-green-500" />
                      <h3 className="text-sm font-bold text-slate-900">Completed Lately</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto p-5 space-y-4">
                      {completedChores.length === 0 ? (
                        <p className="text-xs text-slate-400 text-center py-4">Nothing completed yet</p>
                      ) : (
                        completedChores.slice(-8).reverse().map(chore => {
                          const hex = memberHex(chore.assigned_to);
                          return (
                            <div key={chore.id} className="flex gap-3 items-start">
                              <div className="size-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                   style={{ background: hex }}>
                                {(chore.assigned_to || '?').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-xs text-slate-600 leading-snug">
                                  <span className="font-bold text-slate-900">{chore.assigned_to || 'Someone'}</span>
                                  {' '}completed{' '}
                                  <span className="italic">{chore.title}</span>
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Progress reward */}
                  <div className="rounded-2xl text-white p-6 relative overflow-hidden" style={{ background: '#2b8cee' }}>
                    <div className="absolute -right-4 -bottom-4 opacity-10 pointer-events-none">
                      <Icon name="celebration" className="text-9xl" />
                    </div>
                    <h3 className="text-sm font-bold mb-1">Household Progress</h3>
                    <p className="text-xs mb-5 opacity-80">Keep up the great work! 🎉</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-bold">
                        <span>PROGRESS</span>
                        <span>{totalDone}/{totalAll} TASKS</span>
                      </div>
                      <div className="w-full h-3 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.2)' }}>
                        <div className="h-full rounded-full transition-all duration-1000"
                             style={{ width: `${rewardPct}%`, background: 'white', boxShadow: '0 0 12px rgba(255,255,255,0.4)' }} />
                      </div>
                    </div>
                    <p className="mt-4 text-xs opacity-70">
                      {totalAll - totalDone > 0 ? `${totalAll - totalDone} tasks remaining` : 'All done — amazing work!'}
                    </p>
                  </div>

                  {/* Tip */}
                  <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex gap-3 items-start">
                    <div className="p-2 rounded-xl flex-shrink-0" style={{ background: '#fff7ed' }}>
                      <Icon name="lightbulb" style={{ color: '#8e4a00' }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#8e4a00' }}>Tip</p>
                      <p className="text-xs text-slate-500 leading-relaxed italic">
                        Tick off chores as you go to keep the household progress bar climbing!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ---- PHOTOS VIEW ---- */}
          {currentView === 'photos' && (
            <div>
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Photo Gallery</h2>
                  <p className="text-slate-400 text-sm mt-0.5">{dateStr} · {photos.length} photos</p>
                </div>
                <label className="text-white px-5 py-2.5 rounded-full font-semibold flex items-center gap-2 hover:opacity-90 transition-all shadow-lg text-sm cursor-pointer"
                  style={{ background: '#2b8cee' }}>
                  <Icon name="cloud_upload" className="text-xl" />
                  Upload Photos
                  <input type="file" multiple accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                </label>
              </div>

              {/* Filter tabs + count */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex bg-slate-100 p-1 rounded-full">
                  {[['all','All Photos'],['favourites','Favourites'],['albums','Albums']].map(([v, label]) => (
                    <button key={v} onClick={() => setPhotoFilter(v)}
                      className={`text-[10px] font-bold tracking-wider uppercase px-5 py-1.5 rounded-full transition-all ${
                        photoFilter === v ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-500 hover:text-blue-500'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
                <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-4 py-1.5 rounded-full">
                  {photos.length} Items
                </span>
              </div>

              {photos.length === 0 ? (
                <label className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-16 flex flex-col items-center justify-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/20 transition-all group">
                  <Icon name="add_photo_alternate" className="text-5xl text-slate-300 group-hover:text-blue-400 transition-colors block mx-auto mb-4" />
                  <p className="text-slate-400 font-semibold mb-1">No photos yet</p>
                  <p className="text-slate-300 text-sm">Click to upload your first family photo</p>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                </label>
              ) : (
                <>
                  {/* Photo grid — first photo featured large */}
                  <section className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-slate-800 flex items-center gap-2.5">
                        <span className="w-2.5 h-7 rounded-full inline-block" style={{ background: '#2b8cee' }} />
                        Your Photos
                      </h3>
                      <button onClick={() => { setSlideshowIndex(0); setSlideshowActive(true); }}
                        className="flex items-center gap-1.5 text-sm font-semibold text-blue-500 hover:text-blue-600 transition-colors">
                        <Icon name="slideshow" className="text-lg" />
                        Slideshow
                      </button>
                    </div>
                    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(6, 1fr)', gridAutoRows: '150px' }}>
                      {/* Featured first photo */}
                      <div className="group relative rounded-2xl overflow-hidden shadow-sm cursor-pointer border border-slate-100"
                           style={{ gridColumn: 'span 2', gridRow: 'span 2' }}
                           onClick={() => { setSlideshowIndex(0); setSelectedPhoto(photos[0]); }}>
                        <img src={`${API_URL}/uploads/${photos[0].filename}`}
                          alt={photos[0].caption || 'Family photo'}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4">
                          {photos[0].caption && <p className="text-white text-sm font-semibold">{photos[0].caption}</p>}
                        </div>
                        <button onClick={e => { e.stopPropagation(); deletePhoto(photos[0].id); }}
                          className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500">
                          <Icon name="delete" className="text-sm" />
                        </button>
                      </div>
                      {/* Remaining photos */}
                      {photos.slice(1).map((photo, i) => (
                        <div key={photo.id}
                          className="group relative rounded-2xl overflow-hidden shadow-sm cursor-pointer border border-slate-100 bg-white"
                          onClick={() => { setSlideshowIndex(i + 1); setSelectedPhoto(photo); }}>
                          <img src={`${API_URL}/uploads/${photo.filename}`}
                            alt={photo.caption || 'Family photo'}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                          <button onClick={e => { e.stopPropagation(); deletePhoto(photo.id); }}
                            className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500">
                            <Icon name="delete" className="text-sm" />
                          </button>
                          {photo.caption && (
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-white text-xs font-medium truncate">{photo.caption}</p>
                            </div>
                          )}
                        </div>
                      ))}
                      {/* Add more card */}
                      <label className="rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-blue-500 hover:border-blue-300 hover:bg-blue-50/30 transition-all cursor-pointer">
                        <Icon name="add_photo_alternate" className="text-3xl" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Add More</span>
                        <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                      </label>
                    </div>
                  </section>

                  {/* People in Photos */}
                  <section className="pb-4">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2.5 mb-5">
                      <span className="w-2.5 h-7 rounded-full inline-block" style={{ background: '#f59e0b' }} />
                      People in Photos
                    </h3>
                    <div className="flex gap-8 overflow-x-auto pb-2">
                      {(familyMembers.length > 0 ? familyMembers : [
                        { name: 'Jake' }, { name: 'Christie' }, { name: 'Isla' },
                      ]).map((m, i) => {
                        const hex = memberHex(m.name);
                        return (
                          <div key={i} className="flex flex-col items-center gap-2 group cursor-pointer flex-shrink-0">
                            <div className="w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-2xl transition-transform duration-200 group-hover:scale-110"
                                 style={{ background: hex, border: `4px solid ${hex}`, boxShadow: `0 0 0 4px ${hex}33` }}>
                              {m.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-bold text-slate-700">{m.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                </>
              )}
            </div>
          )}

          {/* ---- ADMIN VIEW ---- */}
          {currentView === 'admin' && currentUser?.is_admin && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Admin</h2>
                  <p className="text-slate-500 text-sm mt-1">Manage users and view stats</p>
                </div>
                <button onClick={() => { setEditingUser(null); setUserForm({ name: '', email: '', password: '', is_admin: false }); setShowUserModal(true); }}
                  className="text-white px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 hover:opacity-90 transition-all shadow-md text-sm"
                  style={{ background: '#2b8cee' }}>
                  <Icon name="person_add" className="text-xl" />
                  Add User
                </button>
              </div>
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 mb-8">
                {[
                  { label: 'Users', value: stats.users || 0, icon: 'group', color: 'text-blue-600 bg-blue-50' },
                  { label: 'Events', value: stats.events || 0, icon: 'calendar_today', color: 'text-green-600 bg-green-50' },
                  { label: 'Chores', value: stats.chores || 0, icon: 'assignment_turned_in', color: 'text-amber-600 bg-amber-50' },
                  { label: 'Photos', value: stats.photos || 0, icon: 'photo_library', color: 'text-pink-600 bg-pink-50' },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className={`size-10 rounded-xl flex items-center justify-center mb-3 ${s.color}`}>
                      <Icon name={s.icon} />
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{s.value}</p>
                    <p className="text-slate-500 text-sm">{s.label}</p>
                  </div>
                ))}
              </div>
              {/* Users table */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-900">Users</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {users.map(u => (
                    <div key={u.id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`size-9 rounded-full flex items-center justify-center text-white text-sm font-bold ${memberColor(u.name).avatar}`}>
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-slate-500 text-sm">{u.email}</p>
                        </div>
                        {u.is_admin && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 font-medium ml-2">Admin</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setEditingUser(u); setUserForm({ name: u.name, email: u.email, password: '', is_admin: u.is_admin }); setShowUserModal(true); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors">
                          <Icon name="edit" />
                        </button>
                        {u.id !== currentUser.id && (
                          <button onClick={() => deleteUser(u.id)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                            <Icon name="delete" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ---- LISTS PLACEHOLDER ---- */}
          {currentView === 'lists' && (() => {
            const activeItems   = listItems.filter(i => !i.checked);
            const checkedItems  = listItems.filter(i => i.checked).sort((a, b) => new Date(b.checked_at) - new Date(a.checked_at));
            const categories    = LIST_CATEGORIES.filter(c => activeItems.some(i => i.category === c.name));
            const uncategorised = activeItems.length === 0 && categories.length === 0;

            // top shoppers from checked items
            const shopperMap = {};
            checkedItems.forEach(i => { shopperMap[i.added_by] = (shopperMap[i.added_by] || 0) + 1; });
            const topShoppers = Object.entries(shopperMap).sort((a, b) => b[1] - a[1]).slice(0, 3);
            const maxShops    = topShoppers[0]?.[1] || 1;

            return (
              <div className="max-w-6xl mx-auto space-y-6">
                {/* Header + Quick Add */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-3xl font-extrabold" style={{ color: '#2b8cee' }}>Shopping List</h2>
                    <p className="text-slate-500 text-sm mt-0.5">
                      {activeItems.length} item{activeItems.length !== 1 ? 's' : ''} still needed
                    </p>
                  </div>

                  {/* Add item bar */}
                  <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl p-2 shadow-sm w-full md:w-auto md:min-w-[480px]">
                    <input
                      className="flex-1 bg-transparent border-none focus:ring-0 text-sm px-3 placeholder-slate-400"
                      placeholder="Add an item (e.g. Oat Milk)…"
                      value={listInput}
                      onChange={e => setListInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addListItem()}
                    />
                    {/* Category selector */}
                    <select
                      className="text-xs border-none bg-slate-100 rounded-xl px-3 py-2 text-slate-600 focus:ring-0 cursor-pointer"
                      value={listCategory}
                      onChange={e => setListCategory(e.target.value)}>
                      {LIST_CATEGORIES.map(c => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={addListItem}
                      className="text-white p-2 rounded-xl transition-colors"
                      style={{ background: '#2b8cee' }}>
                      <Icon name="add_shopping_cart" className="text-xl" />
                    </button>
                  </div>
                </div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                  {/* Left — category sections */}
                  <div className="lg:col-span-2 space-y-5">
                    {uncategorised && (
                      <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-sm">
                        <Icon name="add_shopping_cart" className="text-5xl text-slate-200 mb-3 block mx-auto" />
                        <p className="text-slate-400 text-sm">Your list is empty. Add your first item above!</p>
                      </div>
                    )}

                    {LIST_CATEGORIES.filter(c => activeItems.some(i => i.category === c.name)).map(cat => {
                      const catItems = activeItems.filter(i => i.category === cat.name);
                      return (
                        <section key={cat.name} className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-6 rounded-full" style={{ background: cat.color }} />
                              <h3 className="font-bold text-base text-slate-800">{cat.name}</h3>
                              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-1">
                                {catItems.length} {catItems.length === 1 ? 'ITEM' : 'ITEMS'}
                              </span>
                            </div>
                          </div>

                          <div className="space-y-1">
                            {catItems.map(item => (
                              <div key={item.id}
                                className="group flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-all">
                                <div className="flex items-center gap-4">
                                  <input type="checkbox" checked={item.checked}
                                    onChange={() => toggleListItem(item.id)}
                                    className="w-5 h-5 rounded cursor-pointer accent-blue-500" />
                                  <span className={`text-sm text-slate-800 ${item.checked ? 'line-through text-slate-400' : ''}`}>
                                    {item.name}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  {/* Uploader avatar */}
                                  <div className="w-6 h-6 rounded-full border-2 border-white shadow flex items-center justify-center text-[9px] text-white font-bold"
                                    style={{ background: memberHex(item.added_by) }}
                                    title={`Added by ${item.added_by}`}>
                                    {item.added_by.charAt(0).toUpperCase()}
                                  </div>
                                  {/* Delete (visible on hover) */}
                                  <button onClick={() => deleteListItem(item.id)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-300 hover:text-red-500">
                                    <Icon name="delete" className="text-xl" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </section>
                      );
                    })}
                  </div>

                  {/* Right column */}
                  <div className="space-y-5">

                    {/* Recently Purchased */}
                    <section className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                      <h3 className="font-bold text-base text-slate-800 mb-4">Recently Purchased</h3>
                      {checkedItems.length === 0 ? (
                        <p className="text-slate-400 text-xs text-center py-4">Nothing purchased yet.</p>
                      ) : (
                        <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                          {checkedItems.slice(0, 8).map((item, idx) => (
                            <div key={item.id}
                              className="flex items-center gap-3 p-3 rounded-xl bg-slate-50"
                              style={{ opacity: Math.max(0.4, 1 - idx * 0.15) }}>
                              <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 flex-shrink-0">
                                <Icon name="check_circle" className="text-xl" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-500 line-through truncate">{item.name}</p>
                                <p className="text-[11px] text-slate-400 mt-0.5">
                                  Bought {item.checked_at
                                    ? new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
                                        -Math.round((Date.now() - new Date(item.checked_at)) / 60000), 'minute')
                                    : 'just now'} by {item.added_by.split(' ')[0]}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>

                    {/* Top Shoppers */}
                    <section className="rounded-3xl p-6 shadow-lg text-white" style={{ background: '#2b8cee' }}>
                      <h3 className="font-bold text-base mb-4">Top Shoppers</h3>
                      {topShoppers.length === 0 ? (
                        <p className="text-blue-100 text-xs text-center py-2">Check off items to see who's shopping most!</p>
                      ) : (
                        <div className="space-y-3">
                          {topShoppers.map(([name, count]) => (
                            <div key={name} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full border border-blue-300 flex items-center justify-center text-xs font-bold"
                                  style={{ background: memberHex(name) }}>
                                  {name.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm">{name.split(' ')[0]}</span>
                              </div>
                              <span className="font-bold text-sm">{count} item{count !== 1 ? 's' : ''}</span>
                            </div>
                          ))}
                          {/* Progress bar */}
                          <div className="w-full bg-blue-400/40 rounded-full h-1.5 mt-2">
                            <div className="h-full bg-white rounded-full transition-all"
                              style={{ width: `${Math.min(100, (checkedItems.length / Math.max(listItems.length, 1)) * 100)}%` }} />
                          </div>
                          <p className="text-[11px] text-blue-100 text-center">
                            {checkedItems.length} of {listItems.length} items done
                          </p>
                        </div>
                      )}
                    </section>

                    {/* Quick tip card */}
                    <div className="rounded-3xl p-6 border border-slate-200 bg-blue-50 relative overflow-hidden">
                      <div className="relative z-10">
                        <h4 className="font-bold text-sm text-slate-700">Pro Tip</h4>
                        <p className="text-xs text-slate-500 mt-1">Press <kbd className="bg-white border border-slate-200 rounded px-1.5 py-0.5 text-[10px] font-mono">Enter</kbd> after typing an item to add it quickly.</p>
                      </div>
                      <Icon name="tips_and_updates" className="absolute -right-3 -bottom-3 text-[80px] text-blue-100" />
                    </div>

                  </div>
                </div>
              </div>
            );
          })()}

          {/* ---- SETTINGS PLACEHOLDER ---- */}

          {/* ---- SETTINGS PLACEHOLDER ---- */}
          {currentView === 'settings' && (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Icon name="settings" className="text-5xl text-slate-300 block mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-400">Settings</h3>
              <p className="text-slate-400 text-sm mt-1">Coming soon</p>
            </div>
          )}

        </main>

        {/* Footer */}
        <footer className="border-t border-slate-200 bg-white px-8 h-12 flex items-center gap-6 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full inline-block" style={{ background: '#2b8cee' }} />
            <span className="text-xs font-bold text-slate-500 uppercase">{pendingCnt} Tasks Pending</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-green-400 inline-block" />
            <span className="text-xs font-bold text-slate-500 uppercase">{events.length} Events</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-amber-400 inline-block" />
            <span className="text-xs font-bold text-slate-500 uppercase">{photos.length} Photos</span>
          </div>
        </footer>
      </div>
      </div>
      )}

      {/* ============================================================ */}
      {/* SHARED MODALS                                                 */}
      {/* ============================================================ */}

      {/* Quick Add Modal — two tabs: Event + Roster */}
      {showEventModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
             onClick={closeQuickAdd}>
          <div className="bg-white rounded-2xl shadow-2xl w-full"
               style={{ maxWidth: addModalTab === 'roster' ? 640 : 440 }}
               onClick={e => e.stopPropagation()}>

            {/* Tab bar */}
            <div className="flex items-center border-b border-slate-100 px-6 pt-5 pb-0 gap-1">
              {[
                { id: 'event',  icon: 'event',       label: 'Single Event' },
                { id: 'roster', icon: 'date_range',  label: 'Roster / Shifts' },
              ].map(tab => (
                <button key={tab.id} onClick={() => setAddModalTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                    addModalTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-slate-400 hover:text-slate-600'
                  }`}>
                  <Icon name={tab.icon} className="text-base" />
                  {tab.label}
                </button>
              ))}
              <button onClick={closeQuickAdd}
                className="ml-auto mb-2 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                <Icon name="close" />
              </button>
            </div>

            <div className="p-6">

              {/* ---- EVENT TAB ---- */}
              {addModalTab === 'event' && (
                <div className="space-y-4">
                  <Field label="Title">
                    <input className={inputCls} placeholder="Event title" value={eventForm.title}
                      autoFocus
                      onChange={e => setEventForm({ ...eventForm, title: e.target.value })}
                      onKeyDown={e => e.key === 'Enter' && eventForm.title && eventForm.date && addEvent()} />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Date">
                      <input type="date" className={inputCls} value={eventForm.date}
                        onChange={e => setEventForm({ ...eventForm, date: e.target.value })} />
                    </Field>
                    <Field label="Time">
                      <input type="time" className={inputCls} value={eventForm.time}
                        onChange={e => setEventForm({ ...eventForm, time: e.target.value })} />
                    </Field>
                  </div>
                  <Field label="Description (optional)">
                    <textarea className={inputCls} rows={2} placeholder="Add details..."
                      value={eventForm.description}
                      onChange={e => setEventForm({ ...eventForm, description: e.target.value })} />
                  </Field>
                  <button onClick={addEvent} disabled={!eventForm.title || !eventForm.date}
                    className="w-full text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: '#2b8cee' }}>
                    Save Event
                  </button>
                  <p className="text-center text-xs text-slate-400">
                    Adding multiple shifts?{' '}
                    <button className="text-blue-500 font-semibold hover:underline"
                      onClick={() => setAddModalTab('roster')}>Use the Roster tab →</button>
                  </p>
                </div>
              )}

              {/* ---- ROSTER TAB ---- */}
              {addModalTab === 'roster' && (() => {
                const shifts = rosterPerson ? (SHIFT_TEMPLATES[rosterPerson] || []) : [];
                const pickerWeeks = getPickerWeeks(6);
                const todayStr2 = toLocalDate(new Date());
                const personHex = ROSTER_PEOPLE.find(p => p.key === rosterPerson)?.hex || '#2b8cee';

                const toggleDay = (ds) => {
                  const next = new Set(rosterDays);
                  next.has(ds) ? next.delete(ds) : next.add(ds);
                  setRosterDays(next);
                };

                return (
                  <div className="space-y-5">
                    {/* Step 1 — person */}
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">1 · Select person</p>
                      <div className="flex gap-2">
                        {ROSTER_PEOPLE.map(p => (
                          <button key={p.key} onClick={() => { setRosterPerson(p.key); setRosterShift(null); }}
                            className="flex-1 py-2 rounded-xl text-sm font-bold border-2 transition-all"
                            style={rosterPerson === p.key
                              ? { borderColor: p.hex, background: p.hex, color: '#fff' }
                              : { borderColor: '#e2e8f0', color: '#64748b' }}>
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Step 2 — shift type */}
                    {rosterPerson && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">2 · Select shift</p>
                        <div className="flex flex-wrap gap-2">
                          {shifts.map(s => (
                            <button key={s.label} onClick={() => setRosterShift(s)}
                              className="px-3 py-1.5 rounded-lg text-sm font-semibold border-2 transition-all"
                              style={rosterShift?.label === s.label
                                ? { borderColor: personHex, background: personHex + '15', color: personHex }
                                : { borderColor: '#e2e8f0', color: '#64748b' }}>
                              {s.label}
                              <span className="ml-1.5 text-xs opacity-60">{fmtShift(s.time)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Step 3 — day picker */}
                    {rosterShift && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                            3 · Pick days <span className="normal-case font-normal">({rosterDays.size} selected)</span>
                          </p>
                          {rosterDays.size > 0 && (
                            <button onClick={() => setRosterDays(new Set())}
                              className="text-xs text-slate-400 hover:text-red-400 transition-colors">Clear</button>
                          )}
                        </div>
                        <div className="rounded-xl border border-slate-100 overflow-hidden">
                          {/* Day-of-week header */}
                          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-100">
                            {['M','T','W','T','F','S','S'].map((d, i) => (
                              <div key={i} className="py-1.5 text-center text-xs font-bold text-slate-400">{d}</div>
                            ))}
                          </div>
                          {/* Week rows */}
                          {pickerWeeks.map((week, wi) => (
                            <div key={wi} className="grid grid-cols-7 border-b border-slate-50 last:border-b-0">
                              {week.map((day, di) => {
                                const ds = toLocalDate(day);
                                const isPast     = ds < todayStr2;
                                const isSelected = rosterDays.has(ds);
                                const isToday2   = ds === todayStr2;
                                return (
                                  <button key={di} onClick={() => !isPast && toggleDay(ds)} disabled={isPast}
                                    className="py-2 text-center text-xs font-semibold transition-all relative"
                                    style={isSelected
                                      ? { background: personHex, color: '#fff', fontWeight: 700 }
                                      : isPast
                                        ? { color: '#cbd5e1', cursor: 'default' }
                                        : isToday2
                                          ? { color: personHex, fontWeight: 700 }
                                          : { color: '#475569' }}>
                                    {isToday2 && !isSelected && (
                                      <span className="absolute top-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                                            style={{ background: personHex }} />
                                    )}
                                    {day.getDate()}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Preview + Save */}
                    {rosterShift && rosterDays.size > 0 && (
                      <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-bold text-slate-900">
                            {ROSTER_PEOPLE.find(p => p.key === rosterPerson)?.label} · {rosterShift.label}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {rosterDays.size} shift{rosterDays.size !== 1 ? 's' : ''} · {fmtShift(rosterShift.time)} start
                          </p>
                        </div>
                        <button onClick={addRosterShifts} disabled={rosterSaving}
                          className="text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap flex-shrink-0"
                          style={{ background: personHex }}>
                          {rosterSaving ? 'Saving…' : `Save ${rosterDays.size} shift${rosterDays.size !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    )}

                    {!rosterPerson && (
                      <p className="text-center text-sm text-slate-400 py-4">Select a person to get started</p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Add Chore Modal */}
      {showChoreModal && (
        <Modal title="Add Chore" onClose={() => { setShowChoreModal(false); setChoreForm({ title: '', assigned_to: '', due_date: '', notes: '' }); }}>
          <div className="space-y-4">
            <Field label="Title">
              <input className={inputCls} placeholder="Chore title" value={choreForm.title}
                onChange={e => setChoreForm({ ...choreForm, title: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Assigned to">
                <input className={inputCls} placeholder="Name" value={choreForm.assigned_to}
                  onChange={e => setChoreForm({ ...choreForm, assigned_to: e.target.value })} />
              </Field>
              <Field label="Due date">
                <input type="date" className={inputCls} value={choreForm.due_date}
                  onChange={e => setChoreForm({ ...choreForm, due_date: e.target.value })} />
              </Field>
            </div>
            <Field label="Notes (optional)">
              <textarea className={inputCls} rows={2} placeholder="Add notes..." value={choreForm.notes}
                onChange={e => setChoreForm({ ...choreForm, notes: e.target.value })} />
            </Field>
            <button onClick={addChore} disabled={!choreForm.title}
              className="w-full text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: '#2b8cee' }}>
              Save Chore
            </button>
          </div>
        </Modal>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <Modal title="Event" onClose={() => setSelectedEvent(null)}>
          {(() => {
            const c = eventColor(selectedEvent.title);
            return (
              <div>
                <div className={`rounded-xl p-4 mb-4 ${c.pill}`}>
                  <h3 className={`text-lg font-bold ${c.title}`}>{selectedEvent.title}</h3>
                  {selectedEvent.date && (
                    <p className={`text-sm mt-1 flex items-center gap-1 ${c.sub}`}>
                      <Icon name="calendar_today" className="text-base" />
                      {parseDate(selectedEvent.date)}
                    </p>
                  )}
                  {selectedEvent.time && (
                    <p className={`text-sm mt-0.5 flex items-center gap-1 ${c.sub}`}>
                      <Icon name="schedule" className="text-base" />
                      {fmt12(selectedEvent.time)}
                    </p>
                  )}
                </div>
                {selectedEvent.description && (
                  <p className="text-slate-600 text-sm mb-4">{selectedEvent.description}</p>
                )}
                <button onClick={() => deleteEvent(selectedEvent.id)}
                  className="w-full py-2.5 rounded-xl font-semibold text-red-500 border border-red-200 hover:bg-red-50 transition-colors text-sm flex items-center justify-center gap-2">
                  <Icon name="delete" />
                  Delete Event
                </button>
              </div>
            );
          })()}
        </Modal>
      )}

      {/* Photo lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedPhoto(null)}>
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
            onClick={() => setSelectedPhoto(null)}>
            <Icon name="close" />
          </button>
          <img src={`${API_URL}/uploads/${selectedPhoto.filename}`} alt={selectedPhoto.caption || ''}
            className="max-w-full max-h-full object-contain rounded-xl"
            onClick={e => e.stopPropagation()} />
          {selectedPhoto.caption && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-2 rounded-full">
              {selectedPhoto.caption}
            </div>
          )}
          <button onClick={e => { e.stopPropagation(); deletePhoto(selectedPhoto.id); }}
            className="absolute top-4 left-4 p-2 rounded-full bg-red-500/80 text-white hover:bg-red-500 transition-colors">
            <Icon name="delete" />
          </button>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <Modal title={editingUser ? 'Edit User' : 'Add User'} onClose={() => { setShowUserModal(false); setEditingUser(null); }}>
          <div className="space-y-4">
            <Field label="Name">
              <input className={inputCls} placeholder="Full name" value={userForm.name}
                onChange={e => setUserForm({ ...userForm, name: e.target.value })} />
            </Field>
            <Field label="Email">
              <input type="email" className={inputCls} placeholder="Email address" value={userForm.email}
                onChange={e => setUserForm({ ...userForm, email: e.target.value })} />
            </Field>
            {!editingUser && (
              <Field label="Password">
                <input type="password" className={inputCls} placeholder="Password" value={userForm.password}
                  onChange={e => setUserForm({ ...userForm, password: e.target.value })} />
              </Field>
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <div onClick={() => setUserForm({ ...userForm, is_admin: !userForm.is_admin })}
                className={`relative w-10 h-6 rounded-full transition-colors ${userForm.is_admin ? 'bg-blue-500' : 'bg-slate-200'}`}>
                <div className={`absolute top-1 size-4 rounded-full bg-white shadow transition-transform ${userForm.is_admin ? 'translate-x-5' : 'translate-x-1'}`} />
              </div>
              <span className="text-sm font-medium text-slate-700">Admin privileges</span>
            </label>
            <button onClick={handleUserSubmit} disabled={!userForm.name || !userForm.email}
              className="w-full text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: '#2b8cee' }}>
              {editingUser ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── SLIDESHOW FULLSCREEN OVERLAY ── */}
      {slideshowActive && photos.length > 0 && (
        <div className="fixed inset-0 z-[60] bg-black flex flex-col"
          onClick={() => setSlideshowActive(false)}>

          {/* Progress strips */}
          <div className="absolute top-0 left-0 right-0 flex gap-1 p-3 z-10">
            {photos.map((_, i) => (
              <div key={i} className="h-1 flex-1 rounded-full overflow-hidden bg-white/30">
                <div
                  className="h-full bg-white rounded-full transition-all duration-[4000ms] ease-linear"
                  style={{ width: i < slideshowIndex ? '100%' : i === slideshowIndex ? '100%' : '0%',
                           transitionDuration: i === slideshowIndex ? '4000ms' : '0ms' }} />
              </div>
            ))}
          </div>

          {/* Close button */}
          <button
            className="absolute top-4 right-4 z-20 text-white/80 hover:text-white bg-black/40 rounded-full p-2 transition-colors"
            onClick={e => { e.stopPropagation(); setSlideshowActive(false); }}>
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>

          {/* Prev button */}
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 text-white/80 hover:text-white bg-black/40 rounded-full p-3 transition-colors"
            onClick={e => { e.stopPropagation(); setSlideshowIndex(p => (p - 1 + photos.length) % photos.length); }}>
            <span className="material-symbols-outlined text-2xl">chevron_left</span>
          </button>

          {/* Next button */}
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 text-white/80 hover:text-white bg-black/40 rounded-full p-3 transition-colors"
            onClick={e => { e.stopPropagation(); setSlideshowIndex(p => (p + 1) % photos.length); }}>
            <span className="material-symbols-outlined text-2xl">chevron_right</span>
          </button>

          {/* Photo */}
          <div className="flex-1 flex items-center justify-center p-16">
            <img
              src={`${API_URL}/uploads/${photos[slideshowIndex]?.filename}`}
              alt={photos[slideshowIndex]?.caption || ''}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={e => e.stopPropagation()}
            />
          </div>

          {/* Caption + counter */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent flex items-end justify-between">
            <div>
              {photos[slideshowIndex]?.caption && (
                <p className="text-white text-lg font-medium">{photos[slideshowIndex].caption}</p>
              )}
              <p className="text-white/60 text-sm mt-1">
                {new Date(photos[slideshowIndex]?.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
            <span className="text-white/60 text-sm font-medium">
              {slideshowIndex + 1} / {photos.length}
            </span>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
// SHARED UI COMPONENTS
// ============================================================
const inputCls = 'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent';

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
